import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { api, apiClient } from '@/api/apiClient';
import { generateProposalPDF, generateEmployeeListPDF, downloadPDF } from '@/components/utils/pdfGeneration';
import ConfirmDialog from '@/components/ConfirmDialog';
import useConfirmDialog from '@/hooks/useConfirmDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Download,
  Building2,
  Users,
  ArrowRight,
  Mail,
  ClipboardList,
  KanbanSquare,
  RefreshCcw,
  TrendingUp,
  DollarSign,
  Users2,
  Calculator,
  BarChart3,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

function parseCalcError(raw) {
  if (!raw) return { title: 'Calculation failed', detail: 'An unexpected error occurred.', solution: 'Please try again or contact support.' };

  // "Error processing row N (Name): No federal tax config found for year Y, status "S""
  const taxConfigMatch = raw.match(/Error processing row (\d+) \(([^)]+)\): No federal tax config found for year (\d+), status "([^"]+)"/);
  if (taxConfigMatch) {
    const [, rowNum, name, year, status] = taxConfigMatch;
    const statusLabel = { single: 'Single', married: 'Married', head_of_household: 'Head of Household' }[status] || status;
    return {
      title: `Tax rates for ${year} are not set up`,
      detail: `Employee "${name}" (row ${rowNum}) has a filing status of "${statusLabel}", but no federal tax rates exist for the year ${year}.`,
      solution: `Go to Tax Rate Management and add federal tax rates for ${year}, or update the proposal's Tax Year to 2024 or 2025 where rates are already configured.`,
    };
  }

  if (/no census rows/i.test(raw)) {
    return {
      title: 'Census file is empty',
      detail: 'No employee rows were found in the uploaded file.',
      solution: 'Upload a census file that contains at least one employee row and try again.',
    };
  }

  if (/proposal not found/i.test(raw)) {
    return {
      title: 'Proposal no longer exists',
      detail: 'This proposal may have been deleted before the calculation completed.',
      solution: 'Start a new proposal from scratch.',
    };
  }

  // Generic fallback — strip the "Error processing row N (Name):" prefix for readability
  const strippedMsg = raw.replace(/^Error processing row \d+ \([^)]+\):\s*/, '');
  return {
    title: 'Calculation failed',
    detail: strippedMsg,
    solution: 'Review the details above and try again. If the problem persists, contact support.',
  };
}

const REQUIRED_FIELDS = ['full_name', 'annual_salary', 'filing_status', 'state'];
// Fields actually consumed by the Section 125 calculation. "Proposal Mode" restricts
// census data entry to just these, excluding non-essential items like employee email.
const ESSENTIAL_FIELDS = [
  'full_name',
  'annual_salary',
  'filing_status',
  'state',
  'nw4',
  'w4_allowances',
  'w4_step2_multiple_jobs',
  'w4_step3_total',
  'w4_step4c_extra_withholding',
  'pre_tax_monthly'
];
const TARGET_FIELDS = [
  'full_name',
  'email',
  'nw4',
  'filing_status',
  'clm_dep_2019',
  'dependents',
  'w4_allowances',
  'w4_step2_multiple_jobs',
  'w4_step3_total',
  'w4_step4c_extra_withholding',
  'pre_tax_monthly',
  'post_deduct',
  'state',
  'annual_salary',
  'state_filing',
  'emp_number'
];

// Human-readable labels and grouping for the field mapping dropdown.
// Groups: 'Employee' | 'W4 — Calculation Inputs' | 'Deductions' | 'Other'
const FIELD_META = {
  full_name:                    { label: 'Employee Name',                    group: 'Employee',                 desc: 'Full name'                                    },
  email:                        { label: 'Email',                            group: 'Employee',                 desc: 'Work email address'                           },
  annual_salary:                { label: 'Annual Salary',                    group: 'Employee',                 desc: 'Gross annual income'                          },
  filing_status:                { label: 'Filing Status (S / M / H)',        group: 'Employee',                 desc: 'Single, Married, or Head of Household'        },
  state:                        { label: 'State',                            group: 'Employee',                 desc: '2-letter state code, e.g. CA'                 },
  nw4:                          { label: 'NW4 — New W4 (Y / N)',             group: 'W4 — Calculation Inputs',  desc: 'Y = Post-2020 W4, N = Old W4'                 },
  w4_allowances:                { label: 'W4 Allowances / Dependents (#)',   group: 'W4 — Calculation Inputs',  desc: 'Number of allowances claimed on old W4'       },
  dependents:                   { label: 'Dependents',                       group: 'W4 — Calculation Inputs',  desc: 'Number of dependents (old W4)'                },
  w4_step3_total:               { label: 'Step 3 Credits — $ Amount',        group: 'W4 — Calculation Inputs',  desc: 'New W4 Step 3 total dependent credits ($)'    },
  w4_step2_multiple_jobs:       { label: 'Multiple Jobs (Y / N)',             group: 'W4 — Calculation Inputs',  desc: 'New W4 Step 2 — multiple jobs checkbox'       },
  w4_step4c_extra_withholding:  { label: 'Extra Withholding ($)',             group: 'W4 — Calculation Inputs',  desc: 'Additional withholding per paycheck'          },
  pre_tax_monthly:              { label: 'Pre-Tax Monthly Deductions (Box)',  group: 'Deductions',               desc: 'Monthly 401k, qualified health premiums, etc.'},
  post_deduct:                  { label: 'Post-Tax Monthly Deductions',       group: 'Deductions',               desc: 'Post-tax payroll deductions'                  },
  clm_dep_2019:                 { label: 'Claim / Dep 2019',                  group: 'Other',                    desc: 'Legacy 2019 W4 claim / dependent amount'      },
  state_filing:                 { label: 'State Filing Status',               group: 'Other',                    desc: 'State-level filing status if different'        },
  emp_number:                   { label: 'Employee Number / ID',              group: 'Other',                    desc: 'Internal employee ID or payroll number'       },
};

// Ordered group definitions for the dropdown
const FIELD_GROUPS = [
  { key: 'Employee',                desc: null },
  { key: 'W4 — Calculation Inputs', desc: 'Required for Section 125 premium calculation' },
  { key: 'Deductions',              desc: null },
  { key: 'Other',                   desc: null },
];

const STATUS_LABELS = {
  created: 'Created',
  proposed: 'Proposed',
  accepted: 'Accepted',
  denied: 'Denied'
};

const STATUS_COLORS = {
  created: 'bg-slate-100 text-slate-700',
  proposed: 'bg-blue-100 text-blue-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  denied: 'bg-rose-100 text-rose-700'
};

const PIPELINE_SELECTED_KEY = 'proposals_pipeline_selected_id';

// How It Works Visual Component
const HowItWorksFlow = () => {
  const steps = [
    { icon: '💰', title: 'Pre-Tax Deduction', description: 'Employee elects to set aside portion of salary', color: 'bg-blue-50 border-blue-200' },
    { icon: '📉', title: 'Tax Savings', description: 'Reduces federal, FICA & state taxes', color: 'bg-emerald-50 border-emerald-200' },
    { icon: '🏥', title: 'Benefit Access', description: 'Use funds for health, dependent care, transit', color: 'bg-purple-50 border-purple-200' },
    { icon: '✅', title: 'Your Net Benefit', description: 'More take-home pay, lower employer FICA', color: 'bg-amber-50 border-amber-200' }
  ];

  return (
    <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-6 border border-slate-200">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-5 h-5 text-emerald-500" />
        <h3 className="text-lg font-semibold text-slate-800">How Section 125 Works</h3>
      </div>
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
        {steps.map((step, idx) => (
          <React.Fragment key={step.title}>
            <div className={`flex-1 rounded-lg p-4 border-2 ${step.color} text-center`}>
              <span className="text-2xl mb-2 block">{step.icon}</span>
              <p className="font-semibold text-slate-800 text-sm">{step.title}</p>
              <p className="text-xs text-slate-600 mt-1 hidden md:block">{step.description}</p>
            </div>
            {idx < steps.length - 1 && (
              <div className="hidden md:flex items-center justify-center w-8">
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// Average Employee Impact Component
const AverageEmployeeImpact = ({ summary }) => {
  if (!summary) return null;
  
  const avgMonthlyFlex = toSafeNumber(summary.estimatedAvgMonthlyFlex);
  const avgFederalSavings = toSafeNumber(summary.estimatedAvgMonthlyFederalTaxSavings);
  const avgFicaSavings = toSafeNumber(summary.estimatedAvgMonthlyFicaTaxSavings);
  const hasSummaryTaxSplit = avgFederalSavings > 0 || avgFicaSavings > 0;

  const estimatedFederalAndFica = avgMonthlyFlex * 0.2965;
  const displayFederal = hasSummaryTaxSplit ? avgFederalSavings : estimatedFederalAndFica * 0.72;
  const displayFica = hasSummaryTaxSplit ? avgFicaSavings : estimatedFederalAndFica * 0.28;
  
  return (
    <div className="bg-gradient-to-br from-emerald-50 to-white rounded-xl p-6 border border-emerald-200">
      <div className="flex items-center gap-2 mb-4">
        <DollarSign className="w-5 h-5 text-emerald-500" />
        <h3 className="text-lg font-semibold text-slate-800">Average Employee Impact</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white rounded-lg p-4 border border-emerald-100 text-center">
          <p className="text-xl sm:text-2xl font-bold text-emerald-600">{formatCurrency(avgMonthlyFlex)}</p>
          <p className="text-xs text-slate-600 mt-1">Monthly Premium Credit</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-emerald-100 text-center">
          <p className="text-xl sm:text-2xl font-bold text-blue-600">{formatCurrency(displayFederal)}</p>
          <p className="text-xs text-slate-600 mt-1">Federal Tax Savings</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-emerald-100 text-center">
          <p className="text-xl sm:text-2xl font-bold text-purple-600">{formatCurrency(displayFica)}</p>
          <p className="text-xs text-slate-600 mt-1">FICA Savings</p>
        </div>
      </div>
      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800 text-center">
          <span className="font-semibold">💡 Tip:</span> Employees save ~30% in taxes on every dollar set aside through Section 125
        </p>
      </div>
    </div>
  );
};

// Key Metrics Cards for Executive Summary
const KeyMetricsCards = ({ summary }) => {
  const metrics = [
    { label: 'Total Employees', value: summary?.totalRows || 0, type: 'integer', icon: Users2, color: 'text-blue-600', bgColor: 'bg-blue-50' },
    { label: 'Monthly Net Savings', value: summary?.estimatedNetMonthlySavings || 0, type: 'currency', icon: DollarSign, color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
    { label: 'Annual Net Savings', value: summary?.estimatedNetAnnualSavings || 0, type: 'currency', icon: TrendingUp, color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
    { label: 'Avg Monthly Premium', value: summary?.estimatedAvgMonthlyFlex || 0, type: 'currency', icon: Calculator, color: 'text-purple-600', bgColor: 'bg-purple-50' },
  ];

  return (
    <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-4 sm:p-6 border border-slate-200">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-5 h-5 text-emerald-500" />
        <h3 className="text-lg font-semibold text-slate-800">Key Metrics</h3>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          const displayValue = metric.type === 'currency'
            ? formatCurrency(metric.value)
            : formatInteger(metric.value);
          return (
            <div key={metric.label} className={`${metric.bgColor} rounded-lg p-3 sm:p-4 text-center`}>
              <Icon className={`w-5 h-5 ${metric.color} mx-auto mb-2`} />
              <p className="text-lg sm:text-2xl font-bold text-slate-900">{displayValue}</p>
              <p className="text-xs text-slate-600 mt-1">{metric.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

KeyMetricsCards.propTypes = { summary: PropTypes.object };

// Bell Landscaping-style 4-card summary for the Proposal Report
const ProposalReportSummary = ({ summary, notEligibleCount }) => {
  const cards = [
    {
      label: 'Estimated Net Annual Savings',
      value: summary?.estimatedNetAnnualSavings || 0,
      footnote: null,
      color: 'text-emerald-700',
      bg: 'bg-emerald-50 border-emerald-200',
    },
    {
      label: 'Estimated Net Monthly Savings',
      value: summary?.estimatedNetMonthlySavings || 0,
      footnote: null,
      color: 'text-emerald-700',
      bg: 'bg-emerald-50 border-emerald-200',
    },
    {
      label: 'Estimated Average Monthly Premium Credit',
      value: summary?.estimatedAvgMonthlyFlex || 0,
      footnote: notEligibleCount > 0 ? `Not Eligible = ${notEligibleCount} · before employer contribution` : '* before employer contribution',
      color: 'text-blue-700',
      bg: 'bg-blue-50 border-blue-200',
    },
    {
      label: 'Estimated Monthly Employee Benefits; Covered by Premium',
      value: summary?.estimatedTotalMonthlyNetFlex || 0,
      footnote: '* before employer contribution',
      color: 'text-purple-700',
      bg: 'bg-purple-50 border-purple-200',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
      {cards.map((card) => (
        <div key={card.label} className={`rounded-xl border p-3 sm:p-5 text-center ${card.bg}`}>
          <p className={`text-xl sm:text-3xl font-bold ${card.color}`}>{formatCurrency(card.value)}</p>
          <p className="text-xs sm:text-sm font-semibold text-slate-700 mt-2">{card.label}</p>
          {card.footnote && (
            <p className="text-xs text-slate-500 mt-1 hidden sm:block">{card.footnote}</p>
          )}
        </div>
      ))}
    </div>
  );
};

ProposalReportSummary.propTypes = { summary: PropTypes.object, notEligibleCount: PropTypes.number };

const StatusColumn = ({ status, proposals, onStatusChange, onCardClick, selectedProposalId, changingStatusId, censusRowCountByProposalId }) => (
  <div className="bg-slate-50 rounded-lg p-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-semibold text-slate-700">{STATUS_LABELS[status]}</h3>
      <Tip label={`${proposals.length} proposal${proposals.length === 1 ? '' : 's'} in "${STATUS_LABELS[status]}"`}>
        <Badge className={STATUS_COLORS[status]}>{proposals.length}</Badge>
      </Tip>
    </div>
    <Droppable droppableId={status}>
      {(dropProvided, dropSnapshot) => (
        <div
          {...dropProvided.droppableProps}
          ref={dropProvided.innerRef}
          className={`space-y-3 min-h-[48px] rounded-md transition-colors ${dropSnapshot.isDraggingOver ? 'bg-emerald-50 ring-2 ring-emerald-200' : ''}`}
        >
          {proposals.map((proposal, index) => {
            const isChanging = changingStatusId === proposal.id;
            return (
            <Draggable key={proposal.id} draggableId={proposal.id} index={index} isDragDisabled={isChanging}>
              {(dragProvided, dragSnapshot) => {
                const isSelected = selectedProposalId === proposal.id;
                const employeeCount = censusRowCountByProposalId[proposal.id] || 0;
                return (
                  <div
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    {...dragProvided.dragHandleProps}
                    onClick={() => onCardClick(proposal)}
                    className={`relative rounded-lg bg-white border p-3 space-y-2 cursor-pointer hover:shadow-md transition-shadow ${
                      isSelected ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-slate-200'
                    } ${dragSnapshot.isDragging ? 'shadow-lg' : ''}`}
                  >
                    {isChanging && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70">
                        <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-slate-800 flex items-center gap-1.5">
                        {isSelected && <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                        {proposal.company_name}
                      </p>
                      <Tip label={`Status: ${STATUS_LABELS[proposal.status]} • Last updated ${proposal.updated_at ? new Date(proposal.updated_at).toLocaleDateString() : 'unknown'}`}>
                        <Badge className={STATUS_COLORS[proposal.status]}>{STATUS_LABELS[proposal.status]}</Badge>
                      </Tip>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-slate-500">{proposal.contact_email || 'No email'}</p>
                      <Tip label={`${employeeCount} employee record${employeeCount === 1 ? '' : 's'} for this employer`}>
                        <span className="flex items-center gap-1 text-xs text-slate-500 cursor-default">
                          <Users className="w-3.5 h-3.5" />
                          {employeeCount}
                        </span>
                      </Tip>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(STATUS_LABELS).map((target) => (
                        <Button
                          key={target}
                          size="sm"
                          variant={proposal.status === target ? 'default' : 'outline'}
                          disabled={isChanging}
                          onClick={(e) => {
                            e.stopPropagation();
                            onStatusChange(proposal, target);
                          }}
                        >
                          {STATUS_LABELS[target]}
                        </Button>
                      ))}
                    </div>
                  </div>
                );
              }}
            </Draggable>
            );
          })}
          {dropProvided.placeholder}
        </div>
      )}
    </Droppable>
  </div>
);

StatusColumn.propTypes = {
  status: PropTypes.string.isRequired,
  proposals: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    company_name: PropTypes.string,
    status: PropTypes.string,
    contact_email: PropTypes.string,
    employer_id: PropTypes.string,
    updated_at: PropTypes.string
  })).isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onCardClick: PropTypes.func.isRequired,
  selectedProposalId: PropTypes.string,
  changingStatusId: PropTypes.string,
  censusRowCountByProposalId: PropTypes.objectOf(PropTypes.number)
};

const normalizeHeader = (h) => h?.toString().trim().toLowerCase().replaceAll(/[^a-z0-9]+/g, '_').replaceAll(/(^_+|_+$)/g, '');

const normalizeYesNo = (value) => {
  if (value === undefined || value === null) return '';
  const normalized = value.toString().trim().toLowerCase();
  if (['y', 'yes', 'true', '1'].includes(normalized)) return true;
  if (['n', 'no', 'false', '0'].includes(normalized)) return false;
  return '';
};

const normalizeFilingStatus = (value) => {
  if (!value) return 'single'; // Default to single
  const normalized = value.toString().trim().toLowerCase();
  if (normalized.startsWith('m')) return 'married';
  if (normalized.startsWith('s')) return 'single';
  if (normalized.startsWith('h')) return 'single'; // head of household -> single
  // For any other value (like 'democratic', 'republican'), default to single
  return 'single';
};

const normalizeW4Version = (value) => {
  const yn = normalizeYesNo(value);
  if (yn === true) return 'post_2020';
  if (yn === false) return 'pre_2020';
  return '';
};

const toNumberOrEmpty = (value) => {
  if (value === undefined || value === null || value === '') return '';
  const num = Number(value);
  return Number.isFinite(num) ? num : '';
};

const buildDefaultMapping = (headers) => {
  const mapping = {};
  headers.forEach((h) => {
    const normalized = normalizeHeader(h);
    if (TARGET_FIELDS.includes(normalized)) {
      mapping[h] = normalized;
    } else if (normalized === 'name') {
      mapping[h] = 'full_name';
    } else if (normalized === 's_m_h' || normalized === 'smh') {
      mapping[h] = 'filing_status';
    } else if (normalized.includes('exemption')) {
      // Matches "Exemptions", "2019 Exemptions", "Exemptions (2019)", etc. — a
      // plain equality check missed any header with a year prefix/suffix,
      // silently dropping pre-2020 W4 allowances/dependents from the census.
      mapping[h] = 'w4_allowances';
    } else if (normalized === 'step_2_box_y_n' || normalized === 'step2_box' || normalized === 'step2') {
      mapping[h] = 'w4_step2_multiple_jobs';
    } else if (normalized === 'step_3_total' || normalized === 'step3_total' || normalized === 'step3') {
      mapping[h] = 'w4_step3_total';
    } else if (normalized === 'clm_dep_2019' || normalized === 'clm_dep') {
      mapping[h] = 'dependents';
    } else if (normalized === 'pre_deduct' || normalized === 'pre_deducted' || normalized === 'pre_deduction' || normalized === 'pre_t' || normalized === 'pre_tax' || normalized === 'pre_tax_monthly') {
      mapping[h] = 'pre_tax_monthly';
    } else if (normalized === 'post_deduct') {
      mapping[h] = 'post_deduct';
    } else if (normalized === 'extra_w2' || normalized === 'extra_w4' || normalized === 'extra_withholding') {
      mapping[h] = 'w4_step4c_extra_withholding';
    } else if (normalized === 'extra') {
      mapping[h] = 'w4_step4c_extra_withholding';
    } else if (normalized === 'salary') {
      mapping[h] = 'annual_salary';
    } else if (normalized === 'nw4') {
      mapping[h] = 'nw4';
    } else if (normalized === 'state_filing') {
      mapping[h] = 'state_filing';
    } else if (normalized === 'emp_number') {
      mapping[h] = 'emp_number';
    } else {
      mapping[h] = '';
    }
  });
  return mapping;
};

const applyMapping = (data, mapping) => {
  return data.map((row) => {
    const mapped = /** @type {Record<string, any>} */ ({});
    Object.entries(mapping).forEach(([source, target]) => {
      if (!target) return;
      const value = row[source];
      mapped[target] = value ?? '';
    });

    if (mapped.nw4) {
      mapped.w4_version = normalizeW4Version(mapped.nw4) || mapped.nw4;
    }

    if (mapped.w4_step2_multiple_jobs !== undefined) {
      const normalized = normalizeYesNo(mapped.w4_step2_multiple_jobs);
      mapped.w4_step2_multiple_jobs = normalized === '' ? mapped.w4_step2_multiple_jobs : normalized;
    }

    if (mapped.filing_status) {
      mapped.filing_status = normalizeFilingStatus(mapped.filing_status);
    }

    if (mapped.w4_allowances !== undefined) {
      mapped.w4_allowances = toNumberOrEmpty(mapped.w4_allowances);
    }

    if (mapped.dependents !== undefined) {
      mapped.dependents = toNumberOrEmpty(mapped.dependents);
    }


    if (
      (mapped.w4_allowances === undefined || mapped.w4_allowances === '') &&
      mapped.dependents !== undefined &&
      mapped.dependents !== ''
    ) {
      mapped.w4_allowances = toNumberOrEmpty(mapped.dependents);
    }

    if (mapped.w4_step3_total !== undefined) {
      mapped.w4_step3_total = toNumberOrEmpty(mapped.w4_step3_total);
    }

    if (mapped.w4_step4c_extra_withholding !== undefined) {
      mapped.w4_step4c_extra_withholding = toNumberOrEmpty(mapped.w4_step4c_extra_withholding);
    }

    if (mapped.pre_tax_monthly !== undefined) {
      mapped.pre_tax_monthly = toNumberOrEmpty(mapped.pre_tax_monthly);
    }

    if (mapped.annual_salary !== undefined) {
      mapped.annual_salary = toNumberOrEmpty(mapped.annual_salary);
    }

    if (mapped.flex_allocation !== undefined) {
      mapped.flex_allocation = toNumberOrEmpty(mapped.flex_allocation);
    }

    return mapped;
  });
};

const validateRows = (rows) => {
  const errors = [];
  rows.forEach((row, idx) => {
    REQUIRED_FIELDS.forEach((field) => {
      if (row[field] === undefined || row[field] === null || row[field].toString().trim() === '') {
        errors.push(`Row ${idx + 1}: Missing ${field}`);
      }
    });

    const salary = Number(row.annual_salary);
    if (!Number.isFinite(salary) || salary <= 0) {
      errors.push(`Row ${idx + 1}: annual_salary must be a positive number`);
    }

    const filing = String(row.filing_status || '').toLowerCase();
    if (!['single', 'married'].includes(filing)) {
      errors.push(`Row ${idx + 1}: filing_status must map to single or married`);
    }

    const state = String(row.state || '').trim();
    if (state.length < 2) {
      errors.push(`Row ${idx + 1}: state is invalid`);
    }

    if (
      (row.dependents === undefined || row.dependents === '' || row.dependents === null) &&
      row.clm_dep_2019 !== undefined &&
      row.clm_dep_2019 !== '' &&
      row.clm_dep_2019 !== null
    ) {
      row.dependents = toNumberOrEmpty(row.clm_dep_2019);
    }
  });
  return errors;
};

const buildTemplateCsv = () => {
  const headers = [
    'emp_number',
    'name',
    'email',
    'nw4',
    's m h',
    'clm_dep_2019',
    'exemptions',
    'step 2 box y/n',
    'step 3 total',
    'pre deduct',
    'post deduct',
    'extra',
    'state',
    'salary',
    'state_filing'
  ];
  return `${headers.join(',')}\n`;
};

const toSafeNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

/** @param {unknown} value */
const Tip = (/** @type {{ label: import('react').ReactNode, children: import('react').ReactElement, side?: string }} */ { label, children, side = 'top' }) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side={side}>{label}</TooltipContent>
  </Tooltip>
);

const formatCurrency = (value) =>
  `$${toSafeNumber(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** @param {unknown} value */
const formatPercent = (value) =>
  `${toSafeNumber(value).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

/** @param {unknown} value */
const formatInteger = (value) =>
  toSafeNumber(value).toLocaleString('en-US', { maximumFractionDigits: 0 });

  const computeEmployerNet = (row, settings) => {
    const salary = Number(row.annual_salary || 0);
    const sec125 = Number(row.flex_allocation || 0);
    const monthlySec125 = sec125;
    const claimPercent = Number(settings.claim_percent || 0.86);
    const claim = monthlySec125 * claimPercent;

    let ssReduce = 0;
    if (settings?.apply_ss_reduce) {
      if (salary >= 250000) {
        ssReduce = 235;
      } else if (salary > 188100) {
        ssReduce = 142.5;
      } else if (salary >= 132900) {
        ssReduce = 100;
      }
    }

    const employerFee = Number(settings.employer_fee ?? 60);
    const hasSection125 = monthlySec125 > 0;
    const fee = hasSection125 ? employerFee : 0;
    const employerGross = hasSection125 ? (monthlySec125 * 0.0765 - fee - ssReduce) : 0;

    const netFlex = row.net_flex || 0;
    const contribMin = Number(settings.contrib_min ?? 60);
    const hmra = Number(settings.hmra_amount ?? 0);
    let contrib = 0;

    if (!hasSection125) {
      contrib = hmra;
    } else if (netFlex <= contribMin - 1) {
      contrib = (contribMin - netFlex) + hmra;
    } else {
      contrib = hmra;
    }

    const employerNet = employerGross - contrib;
    return {
      claim,
      employerGross,
      fee,
      contrib,
      employerNet,
      annualized: employerNet * 12
    };
  };

export default function Proposals() {
  const [tab, setTab] = useState('pipeline');
  const [loading, setLoading] = useState(true);
  const [employers, setEmployers] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [allCensusRows, setAllCensusRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [changingStatusId, setChangingStatusId] = useState(null);
  const selectionRestoredRef = useRef(false);
  const { confirmDialogProps, openConfirm } = useConfirmDialog();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
  const [censusRows, setCensusRows] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employeeSort, setEmployeeSort] = useState({ key: 'full_name', dir: 'asc' });
  const [employeePdfLoading, setEmployeePdfLoading] = useState(false);
  const [employeeEmailLoading, setEmployeeEmailLoading] = useState(false);
  const [proposalPdfLoading, setProposalPdfLoading] = useState(false);
  const [employeeListPdfLoading, setEmployeeListPdfLoading] = useState(false);

  const sortedEmployeeRows = useMemo(() => {
    const filtered = censusRows.filter(r =>
      !employeeSearch ||
      (r.full_name || '').toLowerCase().includes(employeeSearch.toLowerCase()) ||
      (r.email || '').toLowerCase().includes(employeeSearch.toLowerCase()) ||
      (r.state || r.raw_data?.state || '').toLowerCase().includes(employeeSearch.toLowerCase())
    );
    return [...filtered].sort((a, b) => {
      const dir = employeeSort.dir === 'asc' ? 1 : -1;
      let aVal = a[employeeSort.key] ?? '';
      let bVal = b[employeeSort.key] ?? '';
      if (employeeSort.key === 'net_flex_annual') {
        aVal = Number(a.net_flex) * 12;
        bVal = Number(b.net_flex) * 12;
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') return (aVal - bVal) * dir;
      return String(aVal).localeCompare(String(bVal)) * dir;
    });
  }, [censusRows, employeeSearch, employeeSort]);

  const handleEmployeeListPdfDownload = async () => {
    if (!selectedProposal) return;
    if (sortedEmployeeRows.length === 0) {
      toast.warning('No census data to export');
      return;
    }
    setEmployeeListPdfLoading(true);
    try {
      const pdf = await generateEmployeeListPDF(selectedProposal, sortedEmployeeRows);
      downloadPDF(pdf, `employee_list_${selectedProposal.reference_id || selectedProposal.id}.pdf`);
      toast.success('Employee list PDF downloaded');
    } catch (err) {
      console.error('[handleEmployeeListPdfDownload] Error:', err);
      toast.error('Failed to generate employee list PDF');
    } finally {
      setEmployeeListPdfLoading(false);
    }
  };

  const [step, setStep] = useState(1);
  const [employerMode, setEmployerMode] = useState('existing');
  const [selectedEmployer, setSelectedEmployer] = useState('');
  const [employerForm, setEmployerForm] = useState({
    company_name: '',
    company_address: '',
    company_city: '',
    company_state: '',
    company_zipcode: '',
    contact_name: '',
    contact_email: '',
    contact_phone: ''
  });
  const [proposalSettings, setProposalSettings] = useState({
    program_type: 'fully',
    tax_year: 2024,
    tax_floor: 10,
    claim_percent: 0.86,
    hmra_amount: 0,
    contrib_min: 0,
    employer_fee: 60,
    salary_cap: 130000,
    apply_ss_reduce: false
  });

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [columnHeaders, setColumnHeaders] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  // Off by default: Proposal Mode hides every non-essential field from the
  // mapping dropdown entirely (not just auto-mapping), so defaulting it on
  // silently made fields like Post-Tax Monthly Deductions unmappable.
  const [proposalMode, setProposalMode] = useState(false);
  const [mappingConfirmed, setMappingConfirmed] = useState(false);
  const [mappedData, setMappedData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatusMessage, setImportStatusMessage] = useState('');
  const [calcError, setCalcError] = useState(null);
  const [proposalResult, setProposalResult] = useState(null);
  const [proposalNote, setProposalNote] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [recalcProgress, setRecalcProgress] = useState(0);
  const [recalcStatus, setRecalcStatus] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [lastVerified, setLastVerified] = useState(null);
  const [statsAccuracy, setStatsAccuracy] = useState(null); // null = not checked, true = accurate, false = inaccurate

  const calcPollIntervalRef = useRef(null);
  const calcToastIdRef = useRef(null);
  const tableScrollRef = useRef(null);
  const scrollRafRef = useRef(null);
  const [editingProposal, setEditingProposal] = useState(false);
  const [editProposalForm, setEditProposalForm] = useState({
    company_name: '',
    company_address: '',
    company_city: '',
    company_state: '',
    company_zipcode: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    program_type: 'fully',
    tax_year: 2024,
    tax_floor: 10,
    claim_percent: 0.86,
    hmra_amount: 0,
    contrib_min: 0,
    employer_fee: 60,
    salary_cap: 130000,
    apply_ss_reduce: false
  });

  const mappedPreviewData = mappedData || (extractedData ? applyMapping(extractedData, fieldMapping) : null);
  const previewRow = mappedPreviewData?.[0] || null;

  const autoFieldMapping = useMemo(() => {
    const mapping = buildDefaultMapping(columnHeaders);
    if (!proposalMode) return mapping;
    // Proposal Mode: only auto-map essential fields, leave the rest ignored
    const filtered = {};
    Object.entries(mapping).forEach(([header, field]) => {
      if (ESSENTIAL_FIELDS.includes(field)) filtered[header] = field;
    });
    return filtered;
  }, [columnHeaders, proposalMode]);
  const autoMappedCount = columnHeaders.filter((h) => fieldMapping[h] && fieldMapping[h] === autoFieldMapping[h]).length;
  // Fast path: every required field was found by auto-mapping and nothing has
  // been manually overridden yet, so the review table below is optional.
  const requiredFieldsAutoMapped = columnHeaders.length > 0
    && REQUIRED_FIELDS.every((f) => Object.values(autoFieldMapping).includes(f))
    && columnHeaders.every((h) => (fieldMapping[h] || '') === (autoFieldMapping[h] || ''));
  const [showFullMappingTable, setShowFullMappingTable] = useState(false);

  const toggleProposalMode = (checked) => {
    setProposalMode(checked);
    if (checked) {
      // Strip any previously-selected non-essential fields (e.g. email) so
      // Proposal Mode genuinely excludes them from data entry.
      setFieldMapping((prev) => {
        const next = {};
        Object.entries(prev).forEach(([header, field]) => {
          if (ESSENTIAL_FIELDS.includes(field)) next[header] = field;
        });
        return next;
      });
    }
  };

  useEffect(() => {
    loadInitial();
  }, []);

  // Persist the selected pipeline card across refreshes — switching tabs
  // within the page already keeps selection via component state, but a full
  // page reload would otherwise silently drop it. Guarded on
  // selectionRestoredRef so this doesn't fire on the very first render
  // (selectedProposal starts null) and wipe the saved id before the restore
  // effect below has had a chance to read it.
  useEffect(() => {
    if (selectedProposal?.id) {
      localStorage.setItem(PIPELINE_SELECTED_KEY, selectedProposal.id);
    } else if (selectionRestoredRef.current) {
      localStorage.removeItem(PIPELINE_SELECTED_KEY);
    }
  }, [selectedProposal]);

  useEffect(() => {
    if (selectionRestoredRef.current || !proposals.length) return;
    selectionRestoredRef.current = true;
    const savedId = localStorage.getItem(PIPELINE_SELECTED_KEY);
    if (!savedId) return;
    const match = proposals.find((p) => p.id === savedId);
    if (match) {
      handleCardClick(match);
    } else {
      localStorage.removeItem(PIPELINE_SELECTED_KEY);
    }
  }, [proposals]);

  useEffect(() => {
    const loadCensusRows = async () => {
      if (!selectedProposal?.id) {
        setCensusRows([]);
        return;
      }
      try {
        const rows = await api.entities.ProposalCensusRow.filter({ proposal_id: selectedProposal.id });
        // Flatten computed_data into the row for easier rendering
        const flattened = (rows || []).map((row) => {
          const raw = row.raw_data || {};
          const computed = row.computed_data || {};
          return {
            id: row.id,
            proposal_id: row.proposal_id,
            row_index: row.row_index,
            full_name: row.full_name || raw.full_name || raw.name || 'Unknown',
            email: row.email || raw.email || '',
            annual_salary: Number(raw.annual_salary) || 0,
            filing_status: raw.filing_status || '',
            filing_status_code: (() => { const fs = (raw.filing_status || '').toLowerCase(); return fs.startsWith('m') ? 'M' : fs.startsWith('h') ? 'H' : 'S'; })(),
            state: raw.state || raw.raw_state || '',
            nw4: raw.w4_version === 'post_2020' ? 'Y' : 'N',
            w4_allowances: Number(raw.w4_allowances ?? 0),
            w4_step3_total: Number(raw.w4_step3_total || 0),
            pre_tax_monthly: Number(raw.pre_tax_monthly || 0),
            flex_allocation: computed.section125_monthly || computed.section125 || 0,
            net_flex: computed.net_flex || 0,
            employer_net: computed.employer_net || 0,
            employer_gross: computed.employer_gross || 0,
            fee: computed.fee || 0,
            contribution: computed.contribution || 0,
            claim: computed.claim || 0,
            federal_tax_savings: computed.federal_tax_savings || computed.federal_savings || 0,
            fica_tax_savings: computed.fica_tax_savings || computed.fica_savings || 0,
            state_tax_savings: computed.state_tax_savings || computed.state_savings || 0,
            total_tax_savings: computed.total_tax_savings || computed.total_savings || ((computed.federal_tax_savings || computed.federal_savings || 0) + (computed.fica_tax_savings || computed.fica_savings || 0) + (computed.state_tax_savings || computed.state_savings || 0)) || 0,
            net_benefit: computed.net_flex || 0,
            annualized: computed.annualized || 0,
            raw_data: raw,
            computed_data: computed
          };
        });
        setCensusRows(flattened);
      } catch (err) {
        console.error('[loadCensusRows] Error:', err);
        setCensusRows([]);
      }
    };
    loadCensusRows();
  }, [selectedProposal?.id]);

  useEffect(() => {
    return () => {
      if (calcPollIntervalRef.current) {
        clearInterval(calcPollIntervalRef.current);
        calcPollIntervalRef.current = null;
      }
      if (calcToastIdRef.current) {
        toast.dismiss(calcToastIdRef.current);
        calcToastIdRef.current = null;
      }
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    };
  }, []);

  const EDGE_ZONE = 80;
  const MAX_SPEED = 12;

  const stopTableScroll = () => {
    if (scrollRafRef.current) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  };

  const handleTableMouseMove = (e) => {
    const el = tableScrollRef.current;
    if (!el) return;
    const { left, right } = el.getBoundingClientRect();
    const x = e.clientX;
    let speed = 0;
    if (x - left < EDGE_ZONE)       speed = -MAX_SPEED * (1 - (x - left) / EDGE_ZONE);
    else if (right - x < EDGE_ZONE) speed =  MAX_SPEED * (1 - (right - x) / EDGE_ZONE);
    stopTableScroll();
    if (speed !== 0) {
      const scroll = () => {
        if (tableScrollRef.current) tableScrollRef.current.scrollLeft += speed;
        scrollRafRef.current = requestAnimationFrame(scroll);
      };
      scrollRafRef.current = requestAnimationFrame(scroll);
    }
  };

  const loadInitial = async () => {
    try {
      const [employersData, proposalsData, censusRowsData] = await Promise.all([
        api.entities.Employer.list(),
        api.entities.Proposal.list(),
        api.entities.ProposalCensusRow.filter({})
      ]);
      setEmployers(employersData || []);
      setProposals(proposalsData || []);
      setAllCensusRows(censusRowsData || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  const filteredProposals = useMemo(() => {
    if (statusFilter === 'all') return proposals;
    return proposals.filter((p) => p.status === statusFilter);
  }, [proposals, statusFilter]);

  // Pipeline cards show how many employees are *in this proposal's census
  // data* — not the real Employee table (which stays empty until the
  // proposal is accepted, see syncEmployeesFromProposalCensus).
  const censusRowCountByProposalId = useMemo(() => {
    const counts = {};
    allCensusRows.forEach((row) => {
      if (!row.proposal_id) return;
      counts[row.proposal_id] = (counts[row.proposal_id] || 0) + 1;
    });
    return counts;
  }, [allCensusRows]);

  const canRecalculate = selectedProposal?.status !== 'denied';

  const handleStatusChange = async (proposalId, status) => {
    setChangingStatusId(proposalId);
    try {
      await api.entities.Proposal.update(proposalId, { status });
      await loadInitial();
      toast.success(`Status updated to "${STATUS_LABELS[status]}"`);
    } catch (err) {
      console.error('[handleStatusChange] Error:', err);
      toast.error('Failed to update status');
    } finally {
      setChangingStatusId(null);
    }
  };

  // Both status-button clicks and drag-and-drop moves go through the same
  // confirmation dialog — a drag that lands on the wrong column (or a slip)
  // shouldn't silently commit a status change.
  const confirmStatusChange = (proposal, target) => {
    if (proposal.status === target) return;
    openConfirm({
      title: `Move to "${STATUS_LABELS[target]}"?`,
      description: `${proposal.company_name} will move from "${STATUS_LABELS[proposal.status]}" to "${STATUS_LABELS[target]}".`,
      confirmLabel: 'Move',
      onConfirm: () => handleStatusChange(proposal.id, target)
    });
  };

  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination || source.droppableId === destination.droppableId) return;
    const proposal = proposals.find((p) => p.id === draggableId);
    if (!proposal) return;
    confirmStatusChange(proposal, destination.droppableId);
  };

  const handleCardClick = async (proposal) => {
    // Clicking the already-selected card unselects it instead of re-fetching.
    if (selectedProposal?.id === proposal.id) {
      setSelectedProposal(null);
      return;
    }

    // Fetch the fresh proposal data with all details including proposal_summary
    try {
      const freshProposal = await api.entities.Proposal.filter({ id: proposal.id });
      if (freshProposal && freshProposal.length > 0) {
        setSelectedProposal(freshProposal[0]);
      } else {
        // Fallback to the proposal from the list
        setSelectedProposal(proposal);
      }

      // Reset verification state
      setStatsAccuracy(null);
      setLastVerified(null);

      // Auto-verify stats accuracy
      verifyStats(proposal.id);
    } catch (err) {
      console.error('[handleCardClick] Error fetching proposal details:', err);
      setSelectedProposal(proposal);
    }
    // Note: no auto-navigation to the Details tab anymore — selecting a card
    // just marks it selected (and reveals the Details/Employees/etc. tabs);
    // the user stays on Pipeline until they choose to open one of those tabs.
  };
  
  const verifyStats = async (proposalId) => {
    setVerifying(true);
    try {
      const response = await apiClient.post(`/proposals/${proposalId}/verify`);
      const { verified, calculatedSummary, storedSummary, updated, discrepancies, recalculatedAt } = response.data;
      
      setStatsAccuracy(verified);
      setLastVerified(recalculatedAt);
      
      // Update selected proposal with corrected summary if needed
      if (updated && selectedProposal?.id === proposalId) {
        setSelectedProposal(prev => ({
          ...prev,
          proposal_summary: calculatedSummary
        }));
        
        if (discrepancies && discrepancies.length > 0) {
          console.warn('[verifyStats] Stats were inaccurate. Discrepancies:', discrepancies);
          toast.warning('Stats were updated to match census row calculations');
        }
      }
      
      // Show accuracy status (but not for minor rounding)
      if (!verified && discrepancies && discrepancies.length > 0) {
        const significantIssues = discrepancies.filter(d => !d.includes('diff=0.00'));
        if (significantIssues.length > 0) {
          toast.error(`Stats mismatch detected: ${significantIssues.length} field(s) need correction`);
        }
      }
    } catch (err) {
      console.error('[verifyStats] Error verifying proposal:', err);
      // Don't show error toast - verification is optional
    } finally {
      setVerifying(false);
    }
  };

  const handleSelectEmployer = (value) => {
    setSelectedEmployer(value);
    const selected = employers.find((e) => e.id === value);
    if (selected) {
      setEmployerForm({
        company_name: selected.company_name || '',
        company_address: selected.address || '',
        company_city: selected.city || '',
        company_state: selected.state || '',
        company_zipcode: selected.zip_code || '',
        contact_name: selected.contact_name || '',
        contact_email: selected.hr_contact_email || '',
        contact_phone: selected.phone || ''
      });
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setExtractedData(null);
      setColumnHeaders([]);
      setFieldMapping({});
      setMappedData(null);
      setMappingConfirmed(false);
      setValidationErrors([]);
      setProposalResult(null);
    }
  };

  const handleUploadAndExtract = async () => {
    if (!file) {
      toast.error('Upload a proposal census file first');
      return;
    }

    setUploading(true);
    try {
      const { file_url } = await api.integrations.Core.UploadFile({ file });
      setUploading(false);
      setProcessing(true);

      const result = await api.integrations.Core.ExtractDataFromUploadedFile({ file_url });

      if (result.status === 'success' && result.output) {
        const rows = Array.isArray(result.output) ? result.output : [result.output];
        setExtractedData(rows);
        const headers = Object.keys(rows[0] || {});
        setColumnHeaders(headers);
        const defaultMapping = buildDefaultMapping(headers);
        if (proposalMode) {
          const filtered = {};
          Object.entries(defaultMapping).forEach(([header, field]) => {
            if (ESSENTIAL_FIELDS.includes(field)) filtered[header] = field;
          });
          setFieldMapping(filtered);
        } else {
          setFieldMapping(defaultMapping);
        }
        setMappingConfirmed(false);
        setMappedData(null);
        setValidationErrors([]);
        toast.success(`Found ${rows.length} proposal rows`);
      } else {
        toast.error('Failed to extract data from file');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error processing file');
    } finally {
      setUploading(false);
      setProcessing(false);
    }
  };

  const handleConfirmMapping = () => {
    const mapped = applyMapping(extractedData || [], fieldMapping);
    const errors = validateRows(mapped);
    if (errors.length > 0) {
      setValidationErrors(errors);
      toast.error('Fix mapping issues before proceeding');
      return;
    }
    setValidationErrors([]);
    setMappedData(mapped);
    setMappingConfirmed(true);
    toast.success('Mapping confirmed');
  };






  const processProposal = async () => {
    console.log('[processProposal] Starting - mappingConfirmed:', mappingConfirmed, 'mappedData length:', mappedData?.length);
    
    if (!mappingConfirmed || !mappedData?.length) {
      console.warn('[processProposal] Mapping not confirmed or no data');
      toast.error('Confirm mapping first');
      return;
    }

    if (!employerForm.company_name || !employerForm.contact_email) {
      console.warn('[processProposal] Missing employer details');
      toast.error('Employer name and contact email are required');
      return;
    }

    setProcessing(true);
    setCalcError(null);
    setImportProgress(0);
    setImportStatusMessage('Starting calculation...');

    if (calcPollIntervalRef.current) {
      clearInterval(calcPollIntervalRef.current);
      calcPollIntervalRef.current = null;
    }
    if (calcToastIdRef.current) {
      toast.dismiss(calcToastIdRef.current);
      calcToastIdRef.current = null;
    }

    try {
      // Pre-flight auth check (axios interceptor may be out-of-date or missing tokens)
      const currentUser = await api.auth.me();
      if (!currentUser || !['admin', 'sub_admin', 'employer_hr'].includes(currentUser.user_role)) {
        toast.error('You must be signed in as admin/sub_admin/employer_hr to calculate proposals.');
        setProcessing(false);
        api.auth.redirectToLogin();
        return;
      }

      console.log('[processProposal] Starting async calculation for:', employerForm.company_name);
      
      // Call async endpoint
      const response = await apiClient.post('/proposals/calculate-async', {
        companyName: employerForm.company_name,
        companyAddress: employerForm.company_address,
        companyCity: employerForm.company_city,
        companyState: employerForm.company_state,
        companyZipcode: employerForm.company_zipcode,
        contactName: employerForm.contact_name,
        contactEmail: employerForm.contact_email,
        contactPhone: employerForm.contact_phone,
        censusFile: file?.name || null,
        mappedData,
        proposalSettings,
        employerMode,
        selectedEmployer,
        employers
      });

      const { jobId, proposalId } = response.data;
      console.log('[processProposal] Job started:', jobId, 'Proposal:', proposalId);
      const toastId = `proposal-calc-${jobId}`;
      calcToastIdRef.current = toastId;

      toast.loading(
        () => (
          <div className="space-y-1">
            <p className="font-semibold">{employerForm.company_name}</p>
            <p className="text-sm">Starting calculation...</p>
            <div className="w-48 bg-slate-200 h-2 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500" style={{ width: '0%' }}></div>
            </div>
          </div>
        ),
        { id: toastId, duration: Infinity }
      );

      // Show toast with real-time updates
      calcPollIntervalRef.current = setInterval(async () => {
        try {
          const jobStatus = await apiClient.get(`/proposals/job-status/${jobId}`);
          const { status, progress, message, companyName } = jobStatus.data;

          console.log('[processProposal] Job status:', status, progress + '%', message);
          setImportProgress(Number(progress) || 0);
          setImportStatusMessage(message || 'Calculating...');

          if (status === 'completed') {
            if (calcPollIntervalRef.current) {
              clearInterval(calcPollIntervalRef.current);
              calcPollIntervalRef.current = null;
            }
            setImportProgress(100);
            setImportStatusMessage('Proposal calculation complete!');
            setProcessing(false);

            let freshProposal = null;
            try {
              const found = await api.entities.Proposal.filter({ id: proposalId });
              freshProposal = found?.[0] || null;
            } catch (fetchErr) {
              console.error('[processProposal] Failed to fetch completed proposal:', fetchErr);
            }
            setProposalResult(freshProposal);

            const summary = freshProposal?.proposal_summary || {};

            toast.success(
              (t) => (
                <div className="space-y-2">
                  <p className="font-semibold">{companyName}</p>
                  <p className="text-sm">✅ Proposal calculation complete!</p>
                  <div className="w-full bg-green-200 h-2 rounded-full"></div>
                  {freshProposal && (
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-700 border-t border-slate-200 pt-2">
                      <span>Employer Gross / mo</span>
                      <span className="text-right font-medium">${(summary.estimatedTotalMonthlyEmployerGross || 0).toFixed(2)}</span>
                      <span>Fee / mo</span>
                      <span className="text-right font-medium">${(summary.estimatedTotalMonthlyFee || 0).toFixed(2)}</span>
                      <span>Contribution / mo</span>
                      <span className="text-right font-medium">${(summary.estimatedTotalMonthlyContribution || 0).toFixed(2)}</span>
                      <span>Employer Net / mo</span>
                      <span className="text-right font-medium">${(summary.estimatedNetMonthlySavings || 0).toFixed(2)}</span>
                      <span>Annualized</span>
                      <span className="text-right font-medium">${(summary.estimatedNetAnnualSavings || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      toast.dismiss(t.id);
                      setSelectedProposal(null);
                      setTab('pipeline');
                      loadInitial();
                    }}
                    className="w-full"
                  >
                    View Proposal
                  </Button>
                </div>
              ),
              { id: toastId, duration: 60000 }
            );
            setStep(5);
            setTab('pipeline');
            await loadInitial();
          } else if (status === 'failed') {
            if (calcPollIntervalRef.current) {
              clearInterval(calcPollIntervalRef.current);
              calcPollIntervalRef.current = null;
            }
            const errorMsg = jobStatus.data.error || 'Calculation failed';
            setImportStatusMessage(errorMsg);
            setCalcError(errorMsg);
            setProcessing(false);

            toast.dismiss(toastId);
          } else {
            // Still calculating
            toast.loading(
              (t) => (
                <div className="space-y-1">
                  <p className="font-semibold">{companyName}</p>
                  <p className="text-sm">{message}</p>
                  <div className="w-48 bg-slate-200 h-2 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 transition-all"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-slate-400">{progress}%</p>
                </div>
              ),
              { id: toastId, duration: Infinity }
            );
          }
        } catch (err) {
          console.error('[processProposal] Poll error:', err);
        }
      }, 500);
    } catch (err) {
      console.error('[processProposal] Error:', err);

      if (err?.response?.status === 401) {
        toast.error('Session expired or unauthorized. Please log in again.');
        setProcessing(false);
        api.auth.redirectToLogin();
      } else {
        toast.error(`Failed to start calculation: ${err.message || 'Unknown error'}`);
        setProcessing(false);
      }
    }
  };

  const validateEmployerForm = () => {
    const errors = [];
    
    if (!employerForm.company_name?.trim()) {
      errors.push('Company Name is required');
    }
    if (!employerForm.contact_name?.trim()) {
      errors.push('Contact Name is required');
    }
    if (!employerForm.contact_email?.trim()) {
      errors.push('Contact Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(employerForm.contact_email)) {
      errors.push('Contact Email must be a valid email address');
    }
    if (!employerForm.contact_phone?.trim()) {
      errors.push('Contact Phone is required');
    }
    if (!employerForm.company_address?.trim()) {
      errors.push('Address is required');
    }
    if (!employerForm.company_city?.trim()) {
      errors.push('City is required');
    }
    if (!employerForm.company_state?.trim()) {
      errors.push('State is required');
    }
    if (!employerForm.company_zipcode?.trim()) {
      errors.push('Zip Code is required');
    }
    
    // Validate proposal settings
    if (proposalSettings.employer_fee > 100) {
      errors.push(`⚠️ Warning: Employer Fee ($${proposalSettings.employer_fee}/month per employee) may be too high and could result in negative employer net savings. Consider lowering to $0-60.`);
    }
    if (proposalSettings.contrib_min > 100) {
      errors.push(`⚠️ Warning: Contribution Min ($${proposalSettings.contrib_min}) may be too high. Consider lowering to $0-60.`);
    }
    
    return errors;
  };

  const handleStep2Continue = () => {
    const errors = validateEmployerForm();
    
    if (errors.length > 0) {
      console.warn('[Step 2 Validation] Errors:', errors);
      errors.forEach(error => toast.error(error));
      return;
    }
    
    console.log('[Step 2 Validation] All fields valid, proceeding to step 3');
    setStep(3);
  };

  const downloadTemplate = () => {
    const csv = buildTemplateCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'proposal_census_template.csv');
    link.click();
    URL.revokeObjectURL(url);
  };

  const getProposalLink = (proposalToken) => `${window.location.origin}/proposal/${proposalToken}`;

  const handleProposalPdfDownload = async (proposal) => {
    if (!proposal) return;
    setProposalPdfLoading(true);
    try {
      const pdf = await generateProposalPDF(proposal, censusRows);
      downloadPDF(pdf, `proposal_${proposal.reference_id || proposal.id}.pdf`);
      toast.success('Proposal PDF downloaded');
    } catch (err) {
      console.error('[handleProposalPdfDownload] Error:', err);
      toast.error('Failed to generate proposal PDF');
    } finally {
      setProposalPdfLoading(false);
    }
  };

  const copyProposalLink = async () => {
    if (!proposalResult?.public_token) {
      toast.error('No proposal link available');
      return;
    }
    try {
      await navigator.clipboard.writeText(getProposalLink(proposalResult.public_token));
      toast.success('Proposal link copied');
    } catch (err) {
      console.error(err);
      toast.error('Failed to copy proposal link');
    }
  };

  const sendProposalEmail = async () => {
    if (!proposalResult?.contact_email) {
      toast.error('No contact email to send');
      return;
    }

    setSendingEmail(true);
    try {
      const summary = proposalResult.summary || {};
      const proposalLink = getProposalLink(proposalResult.public_token);
      const html = `
        <p>Hi ${proposalResult.contact_name || 'there'},</p>
        <p>Your proposal is ready. Here is a quick summary:</p>
        <ul>
          <li>Estimated Net Monthly Savings: $${summary.estimatedNetMonthlySavings || 0}</li>
          <li>Estimated Net Annual Savings: $${summary.estimatedNetAnnualSavings || 0}</li>
          <li>Average Monthly Premium Credit: $${summary.estimatedAvgMonthlyFlex || 0}</li>
        </ul>
        ${proposalNote ? `<p><strong>Note:</strong> ${proposalNote}</p>` : ''}
        <p>View your proposal online: <a href="${proposalLink}">${proposalLink}</a></p>
      `;

      await api.integrations.Core.SendEmail({
        to: proposalResult.contact_email,
        subject: `Your Benefits Proposal - ${proposalResult.company_name}`,
        html
      });

      await api.entities.Proposal.update(proposalResult.id, { status: 'proposed', proposal_note: proposalNote });
      toast.success('Proposal email sent');
      await loadInitial();
    } catch (err) {
      console.error(err);
      toast.error('Failed to send proposal email');
    } finally {
      setSendingEmail(false);
    }
  };


  const recalcProposal = async () => {
    if (!selectedProposal) {
      toast.error('Select a proposal to recalculate');
      return;
    }

    if (selectedProposal.status === 'denied') {
      toast.error('Denied proposals cannot be recalculated');
      return;
    }

    setRecalculating(true);
    setRecalcProgress(0);
    setRecalcStatus('');

    if (calcPollIntervalRef.current) {
      clearInterval(calcPollIntervalRef.current);
      calcPollIntervalRef.current = null;
    }
    if (calcToastIdRef.current) {
      toast.dismiss(calcToastIdRef.current);
      calcToastIdRef.current = null;
    }

    try {
      // Backend recalc API - mirrors fast upload flow
      const response = await apiClient.post(`/proposals/${selectedProposal.id}/recalculate`, {
        settings: {
          ...proposalSettings,
          program_type: selectedProposal.program_type ?? proposalSettings.program_type,
          tax_year: selectedProposal.tax_year ?? proposalSettings.tax_year,
          tax_floor: Number(selectedProposal.tax_floor ?? proposalSettings.tax_floor),
          claim_percent: Number(selectedProposal.claim_percent ?? proposalSettings.claim_percent),
          hmra_amount: Number(selectedProposal.hmra_amount ?? proposalSettings.hmra_amount),
          contrib_min: Number(selectedProposal.contrib_min ?? proposalSettings.contrib_min),
          employer_fee: Number(selectedProposal.employer_fee ?? proposalSettings.employer_fee),
          salary_cap: Number(selectedProposal.salary_cap ?? 130000),
          apply_ss_reduce: selectedProposal.apply_ss_reduce ?? false
        }
      });

      const { jobId } = response.data;
      console.log('[recalcProposal] Backend job started:', jobId);
      
      const toastId = `recalc-${jobId}`;
      calcToastIdRef.current = toastId;

      toast.loading(
        () => (
          <div className="space-y-1">
            <p className="font-semibold">{selectedProposal.company_name}</p>
            <p className="text-sm">Recalculating (backend optimized)...</p>
            <div className="w-48 bg-slate-200 h-2 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500" style={{ width: '0%' }}></div>
            </div>
          </div>
        ),
        { id: toastId, duration: Infinity }
      );

      // Reuse exact polling logic from processProposal
      calcPollIntervalRef.current = setInterval(async () => {
        try {
          const jobStatus = await apiClient.get(`/proposals/job-status/${jobId}`);
          const { status, progress, message, companyName } = jobStatus.data;

          setRecalcProgress(Number(progress) || 0);
          setRecalcStatus(message || 'Recalculating...');

          if (status === 'completed') {
            clearInterval(calcPollIntervalRef.current);
            calcPollIntervalRef.current = null;
            toast.success(`${companyName} - Recalculation complete!`);
            setRecalculating(false);
            await loadInitial(); // Refresh data
            await verifyStats(selectedProposal.id); // Auto-verify
          } else if (status === 'failed') {
            clearInterval(calcPollIntervalRef.current);
            calcPollIntervalRef.current = null;
            toast.error(`Recalc failed: ${jobStatus.data.error || 'Unknown error'}`);
            setRecalculating(false);
          }
        } catch (err) {
          console.error('[recalcProposal poll] Error:', err);
        }
      }, 500);

    } catch (err) {
      console.error('[recalcProposal] API error:', err);
      toast.error('Failed to start recalculation');
      setRecalculating(false);
    }
  };


  const handleEditProposal = () => {
    console.log('[handleEditProposal] Opening edit mode');
    setEditProposalForm({
      company_name: selectedProposal.company_name || '',
      company_address: selectedProposal.company_address || '',
      company_city: selectedProposal.company_city || '',
      company_state: selectedProposal.company_state || '',
      company_zipcode: selectedProposal.company_zipcode || '',
      contact_name: selectedProposal.contact_name || '',
      contact_email: selectedProposal.contact_email || '',
      contact_phone: selectedProposal.contact_phone || '',
      program_type: selectedProposal.program_type || 'fully',
      tax_year: selectedProposal.tax_year || 2024,
      tax_floor: Number(selectedProposal.tax_floor) || 10,
      claim_percent: Number(selectedProposal.claim_percent) || 0.86,
      hmra_amount: Number(selectedProposal.hmra_amount) || 0,
      contrib_min: Number(selectedProposal.contrib_min) || 0,
      employer_fee: Number(selectedProposal.employer_fee) || 0,
      salary_cap: Number(selectedProposal.salary_cap) || 130000,
      apply_ss_reduce: selectedProposal.apply_ss_reduce || false
    });
    setEditingProposal(true);
  };

  const handleSaveProposalEdit = async () => {
    console.log('[handleSaveProposalEdit] Saving proposal changes');
    
    const errors = [];
    if (!editProposalForm.company_name?.trim()) errors.push('Company Name is required');
    if (!editProposalForm.contact_name?.trim()) errors.push('Contact Name is required');
    if (!editProposalForm.contact_email?.trim()) errors.push('Contact Email is required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editProposalForm.contact_email)) errors.push('Invalid email');
    if (!editProposalForm.contact_phone?.trim()) errors.push('Contact Phone is required');
    if (!editProposalForm.company_address?.trim()) errors.push('Address is required');
    if (!editProposalForm.company_city?.trim()) errors.push('City is required');
    if (!editProposalForm.company_state?.trim()) errors.push('State is required');
    if (!editProposalForm.company_zipcode?.trim()) errors.push('Zip Code is required');
    
    if (editProposalForm.employer_fee > 50) {
      toast.warning('Employer Fee exceeds $50/month - verify this is intentional');
    }
    if (editProposalForm.contrib_min > 100) {
      toast.warning('Contribution Min exceeds $100 - verify this is intentional');
    }

    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      return;
    }

    try {
      const updated = await api.entities.Proposal.update(selectedProposal.id, {
        company_name: editProposalForm.company_name,
        company_address: editProposalForm.company_address,
        company_city: editProposalForm.company_city,
        company_state: editProposalForm.company_state,
        company_zipcode: editProposalForm.company_zipcode,
        contact_name: editProposalForm.contact_name,
        contact_email: editProposalForm.contact_email,
        contact_phone: editProposalForm.contact_phone,
        program_type: editProposalForm.program_type,
        tax_year: editProposalForm.tax_year,
        tax_floor: editProposalForm.tax_floor,
        claim_percent: editProposalForm.claim_percent,
        hmra_amount: editProposalForm.hmra_amount,
        contrib_min: editProposalForm.contrib_min,
        employer_fee: editProposalForm.employer_fee,
        salary_cap: editProposalForm.salary_cap,
        apply_ss_reduce: editProposalForm.apply_ss_reduce
      });

      setSelectedProposal(updated);
      setEditingProposal(false);
      toast.success('Proposal updated - you may want to recalculate with new settings');
      console.log('[handleSaveProposalEdit] Proposal saved successfully');
    } catch (err) {
      console.error('[handleSaveProposalEdit] Error:', err);
      toast.error('Failed to update proposal');
    }
  };

  const handleCancelProposalEdit = () => {
    console.log('[handleCancelProposalEdit] Cancelling edit mode');
    setEditingProposal(false);
  };

const handleDeleteProposal = async () => {
    if (!selectedProposal) return;
    
    // Check user role first
    try {
      const user = await api.auth.me();
      if (!['admin', 'sub_admin'].includes(user.user_role)) {
        toast.error('Only admins can delete proposals');
        return;
      }
    } catch (err) {
      toast.error('Unable to verify permissions');
      return;
    }
    
    if (!window.confirm(`Delete "${selectedProposal.company_name}" proposal? Cannot undo.`)) {
      return;
    }

    try {
      console.log('[handleDeleteProposal] Deleting proposal:', selectedProposal.id);
      
      // Step 1: Delete census rows first
      if (typeof api.entities.ProposalCensusRow.deleteByProposal === 'function') {
        await api.entities.ProposalCensusRow.deleteByProposal(selectedProposal.id);
      }
      
      // Step 2: Delete main proposal
      await api.entities.Proposal.delete(selectedProposal.id);
      
      console.log('[handleDeleteProposal] ✅ Proposal + census deleted');
      
      setSelectedProposal(null);
      setTab('pipeline');
      await loadInitial();
      
      toast.success(`"${selectedProposal.company_name}" deleted successfully`);
    } catch (err) {
      console.error('[handleDeleteProposal] Error:', err);
      
      // Better error messages
      let errorMsg = 'Failed to delete proposal';
      if (err.response?.status === 403) {
        errorMsg = 'Permission denied - admin role required';
      } else if (err.response?.status === 404) {
        errorMsg = 'Proposal not found - may already be deleted';
      } else if (err.message?.includes('Foreign')) {
        errorMsg = 'Database constraint - related records block delete';
      }
      
      toast.error(errorMsg);
    }
  };

  const handleEmployeePdfDownload = async () => {
    if (!selectedEmployee) {
      toast.error('No employee selected');
      return;
    }
    setEmployeePdfLoading(true);
    try {
      const pdf = await generateEmployeeListPDF(selectedProposal, [selectedEmployee]);
      const safeName = (selectedEmployee.full_name || 'employee').toLowerCase().replace(/[^a-z0-9]+/g, '_');
      downloadPDF(pdf, `${safeName}_section125_estimate.pdf`);
      toast.success('Employee PDF downloaded');
    } catch (err) {
      console.error('[handleEmployeePdfDownload] Error:', err);
      toast.error('Failed to generate employee PDF');
    } finally {
      setEmployeePdfLoading(false);
    }
  };

  const handleEmployeeEmailSend = async () => {
    if (!selectedEmployee) {
      toast.error('No employee selected');
      return;
    }
    if (!selectedEmployee.email) {
      toast.error('This employee has no email on file — add one to the census row before sending');
      return;
    }
    setEmployeeEmailLoading(true);
    try {
      const html = `
        <p>Hi ${selectedEmployee.full_name || 'there'},</p>
        <p>Here is your Section 125 benefit estimate${selectedProposal?.company_name ? ` from ${selectedProposal.company_name}` : ''}:</p>
        <ul>
          <li>Monthly Premium Amount: $${Number(selectedEmployee.flex_allocation || 0).toFixed(2)}</li>
          <li>Your Estimated Net Monthly Benefit: $${Number(selectedEmployee.net_flex || 0).toFixed(2)}</li>
          <li>Your Estimated Annual Benefit: $${Number(selectedEmployee.annualized || (selectedEmployee.net_flex || 0) * 12).toFixed(2)}</li>
        </ul>
        <p>This is an estimate only and may change based on final enrollment details.</p>
      `;

      await api.integrations.Core.SendEmail({
        to: selectedEmployee.email,
        subject: `Your Section 125 Benefit Estimate${selectedProposal?.company_name ? ` - ${selectedProposal.company_name}` : ''}`,
        html
      });

      toast.success('Email sent to employee');
    } catch (err) {
      console.error('[handleEmployeeEmailSend] Error:', err);
      toast.error('Failed to send email to employee');
    } finally {
      setEmployeeEmailLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground p-3 sm:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Proposals</h1>
          <p className="text-slate-500">Create and manage employer proposals</p>
        </div>
        <div className="flex gap-2 self-start sm:self-auto">
          <Button onClick={() => { setTab('new'); setStep(1); }}>
            <ClipboardList className="w-4 h-4 mr-2" />
            New Proposal
          </Button>
        </div>
      </div>

      <ConfirmDialog {...confirmDialogProps} />

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <div className="w-full overflow-x-auto">
          <TabsList className="flex w-max min-w-full justify-start h-auto py-1">
            <TabsTrigger value="pipeline" className="flex-shrink-0">
              <KanbanSquare className="w-4 h-4 mr-1.5" />
              <span className="whitespace-nowrap">Pipeline</span>
            </TabsTrigger>
            <TabsTrigger value="new" className="flex-shrink-0">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" />
              <span className="whitespace-nowrap sm:hidden">Wizard</span>
              <span className="whitespace-nowrap hidden sm:inline">Proposal Wizard</span>
            </TabsTrigger>
            {selectedProposal && (
              <>
                <TabsTrigger value="detail" className="flex-shrink-0">
                  <ClipboardList className="w-4 h-4 mr-1.5" />
                  <span className="whitespace-nowrap">Details</span>
                </TabsTrigger>
                <TabsTrigger value="employees" className="flex-shrink-0">
                  <Users className="w-4 h-4 mr-1.5" />
                  <span className="whitespace-nowrap">Employees</span>
                </TabsTrigger>
                <TabsTrigger value="tax-savings" className="flex-shrink-0">
                  <BarChart3 className="w-4 h-4 mr-1.5" />
                  <span className="whitespace-nowrap sm:hidden">Tax</span>
                  <span className="whitespace-nowrap hidden sm:inline">Tax Savings</span>
                </TabsTrigger>
                <TabsTrigger value="your-impact" className="flex-shrink-0">
                  <DollarSign className="w-4 h-4 mr-1.5" />
                  <span className="whitespace-nowrap sm:hidden">Impact</span>
                  <span className="whitespace-nowrap hidden sm:inline">Your Impact</span>
                </TabsTrigger>
                <TabsTrigger value="comparison" className="flex-shrink-0">
                  <BarChart3 className="w-4 h-4 mr-1.5" />
                  <span className="whitespace-nowrap">Comparison</span>
                </TabsTrigger>
              </>
            )}
          </TabsList>
        </div>

        <TabsContent value="pipeline" className="mt-6 space-y-6">
          <Card>
            <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="flex items-center gap-2">
                <KanbanSquare className="w-5 h-5 text-emerald-500" />
                Proposal Pipeline
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                {selectedProposal && (
                  <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md pl-3 pr-1.5 py-1">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap">Selected: <strong>{selectedProposal.company_name}</strong></span>
                    <button
                      type="button"
                      onClick={() => setSelectedProposal(null)}
                      className="p-1 rounded hover:bg-emerald-100 text-emerald-600"
                      aria-label="Clear selection"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Object.keys(STATUS_LABELS).map((status) => (
                      <SelectItem key={status} value={status}>
                        {STATUS_LABELS[status]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <TooltipProvider delayDuration={300}>
                <DragDropContext onDragEnd={handleDragEnd}>
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                    {Object.keys(STATUS_LABELS).map((status) => (
                      <StatusColumn
                        key={status}
                        status={status}
                        proposals={filteredProposals.filter((p) => p.status === status)}
                        onStatusChange={confirmStatusChange}
                        onCardClick={handleCardClick}
                        selectedProposalId={selectedProposal?.id}
                        changingStatusId={changingStatusId}
                        censusRowCountByProposalId={censusRowCountByProposalId}
                      />
                    ))}
                  </div>
                </DragDropContext>
              </TooltipProvider>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="new" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-emerald-500" />
                Proposal Wizard
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                <div className="flex items-center gap-2 sm:gap-3 text-sm w-max min-w-full">
                  {[
                    { num: 1, label: 'Select Employer' },
                    { num: 2, label: 'Company Info' },
                    { num: 3, label: 'Upload Census' },
                    { num: 4, label: 'Map Fields' },
                    { num: 5, label: 'Complete' }
                  ].map((s) => (
                    <div key={s.num} className={`flex items-center gap-1.5 sm:gap-2 ${step >= s.num ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center border flex-shrink-0 ${step >= s.num ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300'}`}>
                        {step > s.num ? <CheckCircle2 className="w-4 h-4" /> : s.num}
                      </div>
                      <span className="hidden sm:inline whitespace-nowrap">{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {step === 1 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-emerald-500" />
                    <h3 className="text-lg font-semibold">Select Employer</h3>
                  </div>
                  <div className="flex gap-2">
                    <Button variant={employerMode === 'existing' ? 'default' : 'outline'} onClick={() => setEmployerMode('existing')}>Existing Employer</Button>
                    <Button variant={employerMode === 'new' ? 'default' : 'outline'} onClick={() => setEmployerMode('new')}>New Employer</Button>
                  </div>
                  {employerMode === 'existing' && (
                    <Select value={selectedEmployer} onValueChange={handleSelectEmployer}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select employer" />
                      </SelectTrigger>
                      <SelectContent>
                        {employers.map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.company_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button
                    onClick={() => {
                      if (employerMode === 'existing' && !selectedEmployer) {
                        toast.error('Select an employer to continue');
                        return;
                      }
                      setStep(2);
                    }}
                    className="mt-2"
                  >
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-emerald-500" />
                    <h3 className="text-lg font-semibold">Employer Information</h3>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600">
                    <p><span className="text-rose-600 font-medium">*</span> indicates required field</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Company Name <span className="text-rose-600">*</span></label>
                      <Input placeholder="Company Name" value={employerForm.company_name} onChange={(e) => setEmployerForm({ ...employerForm, company_name: e.target.value })} className={!employerForm.company_name?.trim() ? 'border-rose-300 focus-visible:ring-rose-200' : ''} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Contact Name <span className="text-rose-600">*</span></label>
                      <Input placeholder="Contact Name" value={employerForm.contact_name} onChange={(e) => setEmployerForm({ ...employerForm, contact_name: e.target.value })} className={!employerForm.contact_name?.trim() ? 'border-rose-300 focus-visible:ring-rose-200' : ''} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Contact Email <span className="text-rose-600">*</span></label>
                      <Input placeholder="Contact Email" type="email" value={employerForm.contact_email} onChange={(e) => setEmployerForm({ ...employerForm, contact_email: e.target.value })} className={!employerForm.contact_email?.trim() ? 'border-rose-300 focus-visible:ring-rose-200' : ''} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Contact Phone <span className="text-rose-600">*</span></label>
                      <Input placeholder="Contact Phone" value={employerForm.contact_phone} onChange={(e) => setEmployerForm({ ...employerForm, contact_phone: e.target.value })} className={!employerForm.contact_phone?.trim() ? 'border-rose-300 focus-visible:ring-rose-200' : ''} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Address <span className="text-rose-600">*</span></label>
                      <Input placeholder="Address" value={employerForm.company_address} onChange={(e) => setEmployerForm({ ...employerForm, company_address: e.target.value })} className={!employerForm.company_address?.trim() ? 'border-rose-300 focus-visible:ring-rose-200' : ''} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">City <span className="text-rose-600">*</span></label>
                      <Input placeholder="City" value={employerForm.company_city} onChange={(e) => setEmployerForm({ ...employerForm, company_city: e.target.value })} className={!employerForm.company_city?.trim() ? 'border-rose-300 focus-visible:ring-rose-200' : ''} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">State <span className="text-rose-600">*</span></label>
                      <Input placeholder="State" value={employerForm.company_state} onChange={(e) => setEmployerForm({ ...employerForm, company_state: e.target.value })} className={!employerForm.company_state?.trim() ? 'border-rose-300 focus-visible:ring-rose-200' : ''} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Zip Code <span className="text-rose-600">*</span></label>
                      <Input placeholder="Zip" value={employerForm.company_zipcode} onChange={(e) => setEmployerForm({ ...employerForm, company_zipcode: e.target.value })} className={!employerForm.company_zipcode?.trim() ? 'border-rose-300 focus-visible:ring-rose-200' : ''} />
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">Proposal Settings</h4>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-slate-700">
                      <p className="font-medium mb-1">💡 Configuration Tips:</p>
                      <ul className="text-xs space-y-1 ml-2">
                        <li>• <strong>Employer Fee:</strong> Cost per employee/month. Typical range: $0-20. Higher fees may result in negative employer savings.</li>
                        <li>• <strong>Contribution Min:</strong> Minimum employer contribution if premium value is low. Typical: $0-60.</li>
                        <li>• <strong>Claim %:</strong> Percentage of premium credits actually used (86% is standard).</li>
                        <li>• <strong>Tax Floor:</strong> Minimum federal tax withheld after Section 125 ($30 typical).</li>
                      </ul>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Tax Year</label>
                        <Input
                          type="number"
                          value={proposalSettings.tax_year}
                          onChange={(e) => setProposalSettings({ ...proposalSettings, tax_year: Number(e.target.value) })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Program Type</label>
                        <Select
                          value={proposalSettings.program_type}
                          onValueChange={(value) => setProposalSettings({ ...proposalSettings, program_type: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fully">Fully Insured</SelectItem>
                            <SelectItem value="self">Self Insured</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Tax Floor ($)</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={proposalSettings.tax_floor}
                          onChange={(e) => setProposalSettings({ ...proposalSettings, tax_floor: Number(e.target.value) })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Claim Percent (0.86 = 86%)</label>
                        <Input
                          type="number"
                          step="0.001"
                          value={proposalSettings.claim_percent}
                          onChange={(e) => setProposalSettings({ ...proposalSettings, claim_percent: Number(e.target.value) })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">HMRA Amount ($)</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={proposalSettings.hmra_amount}
                          onChange={(e) => setProposalSettings({ ...proposalSettings, hmra_amount: Number(e.target.value) })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Contribution Min ($)</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={proposalSettings.contrib_min}
                          onChange={(e) => setProposalSettings({ ...proposalSettings, contrib_min: Number(e.target.value) })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Employer Fee ($/month per employee)</label>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={proposalSettings.employer_fee}
                            onChange={(e) => setProposalSettings({ ...proposalSettings, employer_fee: Number(e.target.value) })}
                          />
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setProposalSettings({ ...proposalSettings, employer_fee: 0 })}
                            title="Set to $0"
                          >
                            Clear
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                    <Button onClick={handleStep2Continue}>
                      Continue <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Upload className="w-5 h-5 text-emerald-500" />
                    <h3 className="text-lg font-semibold">Upload Proposal Census</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={downloadTemplate}>
                      <Download className="w-4 h-4 mr-2" />
                      Download Template
                    </Button>
                    <Input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange} />
                    <Button onClick={handleUploadAndExtract} disabled={uploading || processing}>
                      {uploading || processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Extract Data
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                    {extractedData && (
                      <Button onClick={() => setStep(4)}>
                        Continue <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold">Review Field Mapping</h3>
                    <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
                      <Switch id="proposal-mode" checked={proposalMode} onCheckedChange={toggleProposalMode} />
                      <label htmlFor="proposal-mode" className="text-sm font-medium text-slate-700 cursor-pointer">
                        Proposal Mode
                      </label>
                      <span className="text-xs text-slate-500">— essential fields only, no email</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-700">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {autoMappedCount} of {columnHeaders.length} columns auto-detected
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFieldMapping(autoFieldMapping)}
                    >
                      <RefreshCcw className="w-3.5 h-3.5 mr-1.5" />
                      Reset to Auto-Detected
                    </Button>
                  </div>
                  <p className="text-sm text-slate-500 -mt-2">
                    Columns are mapped automatically from your file headers. Adjust any dropdown below to override a column manually.
                  </p>

                  {requiredFieldsAutoMapped && !showFullMappingTable ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        <p className="font-semibold text-emerald-800">All required fields were detected automatically</p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {REQUIRED_FIELDS.map((f) => (
                          <span key={f} className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700">
                            ✓ {FIELD_META[f]?.label || f}
                          </span>
                        ))}
                      </div>
                      <p className="text-sm text-emerald-700">
                        No manual review needed — every required column was matched from your file headers. You can continue now, or review each column's mapping yourself.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button onClick={handleConfirmMapping}>
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Looks good, continue
                        </Button>
                        <Button variant="outline" onClick={() => setShowFullMappingTable(true)}>
                          Review columns manually
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                  {/* Calculation inputs legend */}
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <p className="text-xs font-semibold text-blue-800 mb-2">Fields required for Section 125 premium calculation:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {['annual_salary', 'filing_status', 'state', 'nw4', 'w4_allowances', 'w4_step3_total', 'pre_tax_monthly'].map((f) => {
                        const mapped = Object.values(fieldMapping).includes(f);
                        return (
                          <span key={f} className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${mapped ? 'bg-emerald-100 text-emerald-700' : 'bg-white text-slate-500 border border-slate-200'}`}>
                            {mapped ? '✓' : '○'} {FIELD_META[f]?.label || f}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>CSV Column</TableHead>
                        <TableHead>Maps To</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {columnHeaders.map((header) => {
                        const mapped = fieldMapping[header];
                        const meta = mapped ? FIELD_META[mapped] : null;
                        const isRequired = REQUIRED_FIELDS.includes(mapped);
                        const isAutoDetected = mapped && mapped === autoFieldMapping[header];
                        return (
                          <TableRow key={header} className={mapped && !isRequired && FIELD_META[mapped]?.group === 'W4 — Calculation Inputs' ? 'bg-blue-50/40' : ''}>
                            <TableCell className="font-medium text-slate-700">{header}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Select
                                  value={fieldMapping[header] || '_ignore_'}
                                  onValueChange={(value) => setFieldMapping({ ...fieldMapping, [header]: value === '_ignore_' ? '' : value })}
                                >
                                  <SelectTrigger className="w-72">
                                    <SelectValue placeholder="— Ignore —" />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-80">
                                    <SelectItem value="_ignore_">— Ignore this column —</SelectItem>
                                    <SelectSeparator />
                                    {FIELD_GROUPS.map((group) => {
                                      const groupFields = TARGET_FIELDS.filter(
                                        (f) => (FIELD_META[f]?.group || 'Other') === group.key
                                          && (!proposalMode || ESSENTIAL_FIELDS.includes(f))
                                      );
                                      if (groupFields.length === 0) return null;
                                      return (
                                        <SelectGroup key={group.key}>
                                          <SelectLabel className={`text-xs font-semibold tracking-wide uppercase ${group.key === 'W4 — Calculation Inputs' ? 'text-blue-700' : 'text-slate-500'}`}>
                                            {group.key}{group.desc ? ` — ${group.desc}` : ''}
                                          </SelectLabel>
                                          {groupFields.map((field) => (
                                            <SelectItem key={field} value={field}>
                                              {FIELD_META[field]?.label || field}
                                              {REQUIRED_FIELDS.includes(field) && <span className="ml-1 text-rose-500">*</span>}
                                            </SelectItem>
                                          ))}
                                        </SelectGroup>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                                {mapped ? (
                                  <span className={`shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${isAutoDetected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                                    {isAutoDetected ? 'Auto' : 'Manual'}
                                  </span>
                                ) : (
                                  <span className="shrink-0 whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                                    Ignored
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-slate-500">
                              {isRequired && <span className="mr-1.5 rounded bg-rose-100 px-1.5 py-0.5 text-xs font-medium text-rose-700">Required</span>}
                              {meta?.group === 'W4 — Calculation Inputs' && <span className="mr-1.5 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">Calculation</span>}
                              {meta?.desc || ''}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  </div>
                  {validationErrors.length > 0 && (
                    <Card className="border-rose-200">
                      <CardContent className="p-4 text-rose-600">
                        {validationErrors.map((error) => (
                          <div key={error} className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            <span>{error}</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                  {previewRow && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Preview (First Row)</CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        {Object.entries(previewRow).map(([key, value]) => (
                          <div key={key} className="flex flex-col border-b pb-2">
                            <span className="text-xs font-medium text-emerald-600 uppercase tracking-wide">{key.replace(/_/g, ' ')}</span>
                            <span className="text-slate-800 mt-1">{value?.toString() || '-'}</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                    </>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={() => setStep(3)}>Back</Button>
                    {!(requiredFieldsAutoMapped && !showFullMappingTable) && (
                      <Button onClick={handleConfirmMapping}>Confirm Mapping</Button>
                    )}
                    <Button onClick={processProposal} disabled={!mappingConfirmed || processing}>
                      {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                      Process Proposal
                    </Button>
                  </div>
                  {processing && (
                    <div className="space-y-2">
                      <Progress value={importProgress} />
                      <p className="text-sm text-slate-600">
                        {importStatusMessage || 'Calculating proposal...'} ({Math.round(importProgress)}%)
                      </p>
                    </div>
                  )}
                  {calcError && !processing && (() => {
                    const { title, detail, solution } = parseCalcError(calcError);
                    return (
                      <div className="rounded-lg border border-rose-300 bg-rose-50 p-4 space-y-2">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0" />
                          <p className="text-sm font-semibold text-rose-700">{title}</p>
                        </div>
                        <p className="text-sm text-rose-600 pl-6">{detail}</p>
                        <div className="pl-6 flex items-start gap-1.5">
                          <span className="text-xs font-semibold text-rose-500 mt-0.5 flex-shrink-0">How to fix:</span>
                          <span className="text-xs text-rose-500">{solution}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {step === 5 && proposalResult && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Proposal Ready</h3>
                  <Card>
                    <CardContent className="p-4 space-y-2">
                      <p><strong>Employer:</strong> {proposalResult.company_name}</p>
                      <p><strong>Avg Monthly Premium Credit:</strong> ${proposalResult.summary?.estimatedAvgMonthlyFlex || 0}</p>
                      <p><strong>Avg Monthly Section 125:</strong> ${proposalResult.summary?.estimatedAvgMonthlySection125 || 0}</p>
                      <p><strong>Employer Gross / mo:</strong> ${proposalResult.summary?.estimatedTotalMonthlyEmployerGross || 0}</p>
                      <p><strong>Fee / mo:</strong> ${proposalResult.summary?.estimatedTotalMonthlyFee || 0}</p>
                      <p><strong>Contribution / mo:</strong> ${proposalResult.summary?.estimatedTotalMonthlyContribution || 0}</p>
                      <p><strong>Employer Net / mo:</strong> ${proposalResult.summary?.estimatedNetMonthlySavings || 0}</p>
                      <p><strong>Annualized:</strong> ${proposalResult.summary?.estimatedNetAnnualSavings || 0}</p>
                      {proposalResult.public_token && (
                        <p className="text-sm text-slate-500">
                          Proposal link: {getProposalLink(proposalResult.public_token)}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                  <Textarea
                    rows={4}
                    placeholder="Optional note to include in the proposal email"
                    value={proposalNote}
                    onChange={(e) => setProposalNote(e.target.value)}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={copyProposalLink}>
                      <Download className="w-4 h-4 mr-2" />
                      Copy Proposal Link
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (proposalResult?.public_token) {
                          window.open(getProposalLink(proposalResult.public_token), '_blank');
                        }
                      }}
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Open Proposal
                    </Button>
                    <Button onClick={sendProposalEmail} disabled={sendingEmail}>
                      {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                      Send Proposal Email
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="detail" className="mt-6 space-y-6">
          {selectedProposal && (
            <>
              <Card>
                <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="truncate">{selectedProposal.company_name}</span>
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1 truncate">
                      {selectedProposal.contact_email} • Created {new Date(selectedProposal.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={STATUS_COLORS[selectedProposal.status]}>
                      {STATUS_LABELS[selectedProposal.status]}
                    </Badge>
                    {editingProposal ? (
                      <>
                        <Button
                          size="sm"
                          onClick={handleSaveProposalEdit}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Save Changes
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelProposalEdit}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleEditProposal}
                        >
                          Edit Details
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={handleDeleteProposal}
                          title="Admin only"
                        >
                          🗑️ Delete
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setTab('pipeline')}>
                          Back to Pipeline
                        </Button>
                      </>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Employer Details */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3">Employer Details</h3>
                    {editingProposal ? (
                      <div className="grid md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Company Name <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={editProposalForm.company_name}
                            onChange={(e) => setEditProposalForm({ ...editProposalForm, company_name: e.target.value })}
                            className={!editProposalForm.company_name?.trim() ? 'border-red-300 bg-red-50' : ''}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Contact Name <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={editProposalForm.contact_name}
                            onChange={(e) => setEditProposalForm({ ...editProposalForm, contact_name: e.target.value })}
                            className={!editProposalForm.contact_name?.trim() ? 'border-red-300 bg-red-50' : ''}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Address <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={editProposalForm.company_address}
                            onChange={(e) => setEditProposalForm({ ...editProposalForm, company_address: e.target.value })}
                            className={!editProposalForm.company_address?.trim() ? 'border-red-300 bg-red-50' : ''}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            City <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={editProposalForm.company_city}
                            onChange={(e) => setEditProposalForm({ ...editProposalForm, company_city: e.target.value })}
                            className={!editProposalForm.company_city?.trim() ? 'border-red-300 bg-red-50' : ''}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            State <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={editProposalForm.company_state}
                            onChange={(e) => setEditProposalForm({ ...editProposalForm, company_state: e.target.value.toUpperCase() })}
                            maxLength="2"
                            className={!editProposalForm.company_state?.trim() ? 'border-red-300 bg-red-50' : ''}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Zip Code <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={editProposalForm.company_zipcode}
                            onChange={(e) => setEditProposalForm({ ...editProposalForm, company_zipcode: e.target.value })}
                            className={!editProposalForm.company_zipcode?.trim() ? 'border-red-300 bg-red-50' : ''}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Email <span className="text-red-500">*</span>
                          </label>
                          <Input
                            type="email"
                            value={editProposalForm.contact_email}
                            onChange={(e) => setEditProposalForm({ ...editProposalForm, contact_email: e.target.value })}
                            className={!editProposalForm.contact_email?.trim() ? 'border-red-300 bg-red-50' : ''}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Phone <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={editProposalForm.contact_phone}
                            onChange={(e) => setEditProposalForm({ ...editProposalForm, contact_phone: e.target.value })}
                            className={!editProposalForm.contact_phone?.trim() ? 'border-red-300 bg-red-50' : ''}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                        <div>
                          <p className="text-xs text-slate-500">Company Name</p>
                          <p className="font-medium text-slate-900">{selectedProposal.company_name}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Contact Name</p>
                          <p className="font-medium text-slate-900">{selectedProposal.contact_name}</p>
                        </div>
                        <div className="md:col-span-2">
                          <p className="text-xs text-slate-500">Address</p>
                          <p className="font-medium text-slate-900">{selectedProposal.company_address}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">City</p>
                          <p className="font-medium text-slate-900">{selectedProposal.company_city}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">State</p>
                          <p className="font-medium text-slate-900">{selectedProposal.company_state}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Zip Code</p>
                          <p className="font-medium text-slate-900">{selectedProposal.company_zipcode}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Email</p>
                          <p className="font-medium text-slate-900">{selectedProposal.contact_email}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Phone</p>
                          <p className="font-medium text-slate-900">{selectedProposal.contact_phone}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Proposal Settings */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3">Proposal Settings</h3>
                    {editingProposal ? (
                      <div className="grid md:grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Program Type</label>
                          <Select value={editProposalForm.program_type} onValueChange={(value) => setEditProposalForm({ ...editProposalForm, program_type: value })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fully">Fully Insured</SelectItem>
                              <SelectItem value="self">Self Insured</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Tax Year</label>
                          <Input type="number" value={editProposalForm.tax_year} onChange={(e) => setEditProposalForm({ ...editProposalForm, tax_year: Number(e.target.value) })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Tax Floor ($)</label>
                          <Input type="number" step="0.01" value={editProposalForm.tax_floor} onChange={(e) => setEditProposalForm({ ...editProposalForm, tax_floor: Number(e.target.value) })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Claim Percent (0.86 = 86%)</label>
                          <Input type="number" step="0.001" value={editProposalForm.claim_percent} onChange={(e) => setEditProposalForm({ ...editProposalForm, claim_percent: Number(e.target.value) })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">HMRA Amount ($)</label>
                          <Input type="number" step="0.01" value={editProposalForm.hmra_amount} onChange={(e) => setEditProposalForm({ ...editProposalForm, hmra_amount: Number(e.target.value) })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Contribution Min ($)</label>
                          <Input type="number" step="0.01" value={editProposalForm.contrib_min} onChange={(e) => setEditProposalForm({ ...editProposalForm, contrib_min: Number(e.target.value) })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Employer Fee ($/month/employee)</label>
                          <Input type="number" step="0.01" value={editProposalForm.employer_fee} onChange={(e) => setEditProposalForm({ ...editProposalForm, employer_fee: Number(e.target.value) })} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Salary Cap ($)</label>
                          <Input type="number" value={editProposalForm.salary_cap} onChange={(e) => setEditProposalForm({ ...editProposalForm, salary_cap: Number(e.target.value) })} />
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center gap-2 h-10">
                            <input type="checkbox" checked={editProposalForm.apply_ss_reduce} onChange={(e) => setEditProposalForm({ ...editProposalForm, apply_ss_reduce: e.target.checked })} />
                            <span className="text-sm font-medium text-slate-700">Apply SS Reduce</span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="grid md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                        <div>
                          <p className="text-xs text-slate-500">Program Type</p>
                          <p className="font-medium capitalize">{selectedProposal.program_type || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Tax Year</p>
                          <p className="font-medium">{selectedProposal.tax_year || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Tax Floor</p>
                          <p className="font-medium">${selectedProposal.tax_floor || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Claim %</p>
                          <p className="font-medium">{((selectedProposal.claim_percent || 0) * 100).toFixed(0)}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Employer Fee</p>
                          <p className="font-medium">${selectedProposal.employer_fee || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Contribution Min</p>
                          <p className="font-medium">${selectedProposal.contrib_min || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">HMRA Amount</p>
                          <p className="font-medium">${selectedProposal.hmra_amount || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Salary Cap</p>
                          <p className="font-medium">${selectedProposal.salary_cap || 130000}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">SS Reduce</p>
                          <p className="font-medium">{selectedProposal.apply_ss_reduce ? 'Yes' : 'No'}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Proposal Summary - Executive View */}
                  <div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-emerald-500" />
                        <h3 className="font-semibold text-slate-900">Executive Summary</h3>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {/* Verification status badge */}
                        {verifying && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Verifying...
                          </span>
                        )}
                        {!verifying && statsAccuracy !== null && (
                          <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${statsAccuracy ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {statsAccuracy ? (
                              <>
                                <CheckCircle2 className="w-3 h-3" />
                                Stats Verified
                              </>
                            ) : (
                              <>
                                <AlertCircle className="w-3 h-3" />
                                Stats Updated
                              </>
                            )}
                          </span>
                        )}
                        {lastVerified && (
                          <span className="text-xs text-slate-400">
                            Last verified: {new Date(lastVerified).toLocaleTimeString()}
                          </span>
                        )}
                        {canRecalculate && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => selectedProposal?.id && verifyStats(selectedProposal.id)}
                            disabled={verifying || !selectedProposal?.id}
                            title="Verify stats match census rows"
                          >
                            {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
                            Verify Stats
                          </Button>
                        )}
                        {canRecalculate && (!selectedProposal.proposal_summary || Object.keys(selectedProposal.proposal_summary || {}).length === 0) && (
                          <Button 
                            size="sm" 
                            onClick={recalcProposal}
                            disabled={recalculating}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            {recalculating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
                            {recalculating ? 'Recalculating...' : 'Recalculate'}
                          </Button>
                        )}
                      </div>
                    </div>
                    {!selectedProposal.proposal_summary || Object.keys(selectedProposal.proposal_summary || {}).length === 0 ? (
                      <Card>
                        <CardContent className="p-6 text-center text-slate-500">
                          <Calculator className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                          <p>Proposal summary not yet calculated. Click "Recalculate" to generate statistics.</p>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="space-y-6">
                        {/* How Section 125 Works */}
                        <HowItWorksFlow />
                        
                        {/* Average Employee Impact */}
                        <AverageEmployeeImpact summary={selectedProposal.proposal_summary} />
                        
                        {/* Key Metrics */}
                        <KeyMetricsCards summary={selectedProposal.proposal_summary} />
                      </div>
                    )}
                  </div>

                  {/* Proposal Note */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3">Proposal Note</h3>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-slate-700 whitespace-pre-wrap">
                        {selectedProposal.proposal_note || 'No notes added yet.'}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-3 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (selectedProposal.public_token) {
                          const link = getProposalLink(selectedProposal.public_token);
                          navigator.clipboard.writeText(link);
                          toast.success('Proposal link copied to clipboard');
                        }
                      }}
                      disabled={!selectedProposal.public_token}
                    >
                      <ClipboardList className="w-4 h-4 mr-2" />
                      Copy Link
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (selectedProposal.public_token) {
                          window.open(getProposalLink(selectedProposal.public_token), '_blank');
                        }
                      }}
                      disabled={!selectedProposal.public_token}
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      View Proposal
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleProposalPdfDownload(selectedProposal)}
                      disabled={proposalPdfLoading}
                    >
                      {proposalPdfLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Download PDF
                    </Button>
                    {canRecalculate && (
                      <Button
                        variant="outline"
                        onClick={recalcProposal}
                        disabled={recalculating}
                      >
                        {recalculating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
                        Recalculate Proposal
                      </Button>
                    )}
                    <Button
                      onClick={async () => {
                        setProposalNote(selectedProposal.proposal_note || '');
                        setProposalResult(selectedProposal);
                        await sendProposalEmail();
                      }}
                      disabled={sendingEmail}
                    >
                      {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                      Send Email
                    </Button>
                  </div>
                  {recalculating && (
                    <div className="relative">
                      <Progress value={recalcProgress} className="h-6" />
                      <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-slate-700">
                        Recalculating {recalcStatus || '...'} ({recalcProgress}%)
                      </div>
                    </div>
                  )}

                  {/* Status Change Buttons */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3">Change Status</h3>
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(STATUS_LABELS).map((status) => (
                        <Button
                          key={status}
                          variant={selectedProposal.status === status ? 'default' : 'outline'}
                          onClick={async () => {
                            await handleStatusChange(selectedProposal.id, status);
                            setSelectedProposal({ ...selectedProposal, status });
                          }}
                        >
                          {STATUS_LABELS[status]}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="tax-savings" className="mt-6 space-y-6">
          {selectedProposal && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-500" />
                  Tax Savings Breakdown
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Detailed view of where employee tax savings come from
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {(() => {
                  if (!censusRows.length) {
                    return (
                      <div className="text-center py-12 text-slate-500">
                        <BarChart3 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No census rows found for this proposal.</p>
                        <p className="text-sm mt-1">
                          Upload or recalculate proposal data to view tax savings metrics.
                        </p>
                      </div>
                    );
                  }

                  const monthlyFederal = censusRows.reduce((sum, row) => sum + toSafeNumber(row.federal_tax_savings), 0);
                  const monthlyFica = censusRows.reduce((sum, row) => sum + toSafeNumber(row.fica_tax_savings), 0);
                  const monthlyState = censusRows.reduce((sum, row) => sum + toSafeNumber(row.state_tax_savings), 0);

                  const annualFederal = monthlyFederal * 12;
                  const annualFica = monthlyFica * 12;
                  const annualState = monthlyState * 12;

                  const monthlyCombined = monthlyFederal + monthlyFica + monthlyState;
                  const annualCombined = annualFederal + annualFica + annualState;

                  const federalPct = annualCombined > 0 ? (annualFederal / annualCombined) * 100 : 0;
                  const ficaPct = annualCombined > 0 ? (annualFica / annualCombined) * 100 : 0;
                  const statePct = annualCombined > 0 ? (annualState / annualCombined) * 100 : 0;

                  const categories = [
                    {
                      name: 'Federal Tax Savings',
                      monthly: monthlyFederal,
                      annual: annualFederal,
                      color: 'text-blue-700',
                      bg: 'bg-blue-50 border-blue-200'
                    },
                    {
                      name: 'FICA Savings',
                      monthly: monthlyFica,
                      annual: annualFica,
                      color: 'text-emerald-700',
                      bg: 'bg-emerald-50 border-emerald-200'
                    },
                    {
                      name: 'State Tax Savings',
                      monthly: monthlyState,
                      annual: annualState,
                      color: 'text-purple-700',
                      bg: 'bg-purple-50 border-purple-200'
                    }
                  ];

                  return (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {categories.map((cat) => (
                          <div key={cat.name} className={`rounded-lg border p-4 ${cat.bg}`}>
                            <p className="font-medium text-slate-800">{cat.name}</p>
                            <div className="mt-3 space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-600">Monthly</span>
                                <span className={`font-semibold ${cat.color}`}>{formatCurrency(cat.monthly)}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-600">Annual</span>
                                <span className={`font-semibold ${cat.color}`}>{formatCurrency(cat.annual)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <p className="font-medium text-slate-800 mb-3">Combined Tax Savings</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div className="rounded bg-white border p-3">
                            <p className="text-xs text-slate-500">Monthly Total</p>
                            <p className="text-xl font-bold text-slate-900">{formatCurrency(monthlyCombined)}</p>
                          </div>
                          <div className="rounded bg-white border p-3">
                            <p className="text-xs text-slate-500">Annual Total</p>
                            <p className="text-xl font-bold text-slate-900">{formatCurrency(annualCombined)}</p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-slate-600">Federal</span>
                              <span className="font-medium">{formatPercent(federalPct)}</span>
                            </div>
                            <div className="h-2 rounded bg-slate-200 overflow-hidden">
                              <div className="h-full bg-blue-500" style={{ width: `${federalPct}%` }} />
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-slate-600">FICA</span>
                              <span className="font-medium">{formatPercent(ficaPct)}</span>
                            </div>
                            <div className="h-2 rounded bg-slate-200 overflow-hidden">
                              <div className="h-full bg-emerald-500" style={{ width: `${ficaPct}%` }} />
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-slate-600">State</span>
                              <span className="font-medium">{formatPercent(statePct)}</span>
                            </div>
                            <div className="h-2 rounded bg-slate-200 overflow-hidden">
                              <div className="h-full bg-purple-500" style={{ width: `${statePct}%` }} />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead className="font-semibold">Employee</TableHead>
                              <TableHead className="text-right font-semibold">Federal / mo</TableHead>
                              <TableHead className="text-right font-semibold">FICA / mo</TableHead>
                              <TableHead className="text-right font-semibold">State / mo</TableHead>
                              <TableHead className="text-right font-semibold">Total / mo</TableHead>
                              <TableHead className="text-right font-semibold">Total / yr</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {censusRows.map((row, idx) => {
                              const federal = toSafeNumber(row.federal_tax_savings);
                              const fica = toSafeNumber(row.fica_tax_savings);
                              const state = toSafeNumber(row.state_tax_savings);
                              const totalMonthly = federal + fica + state;
                              return (
                                <TableRow key={row.id || `${row.full_name || 'employee'}-${idx}`}>
                                  <TableCell className="font-medium">{row.full_name || 'Unknown'}</TableCell>
                                  <TableCell className="text-right">{formatCurrency(federal)}</TableCell>
                                  <TableCell className="text-right">{formatCurrency(fica)}</TableCell>
                                  <TableCell className="text-right">{formatCurrency(state)}</TableCell>
                                  <TableCell className="text-right font-semibold text-slate-900">{formatCurrency(totalMonthly)}</TableCell>
                                  <TableCell className="text-right font-semibold text-emerald-700">{formatCurrency(totalMonthly * 12)}</TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                              <TableCell>TOTAL</TableCell>
                              <TableCell className="text-right">{formatCurrency(monthlyFederal)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(monthlyFica)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(monthlyState)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(monthlyCombined)}</TableCell>
                              <TableCell className="text-right text-emerald-800">{formatCurrency(annualCombined)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="your-impact" className="mt-6 space-y-6">
          {selectedProposal && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-500" />
                  Your Financial Impact
                </CardTitle>
                <p className="text-sm text-slate-500">
                  What your company saves from this Section 125 proposal
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {(() => {
                  const monthlyFicaSavings = censusRows.reduce((sum, row) => sum + Number(row.fica_tax_savings || 0), 0);
                  const monthlyFee = censusRows.reduce((sum, row) => sum + Number(row.fee || 0), 0);
                  const monthlyContrib = censusRows.reduce((sum, row) => sum + Number(row.contribution || 0), 0);
                  const monthlyGrossBenefit = monthlyFicaSavings - monthlyFee;
                  const monthlyNetBenefit = monthlyGrossBenefit - monthlyContrib;
                  const annualInvestment = monthlyFee * 12;
                  const annualReturn = monthlyNetBenefit * 12;
                  const roiPercent = annualInvestment > 0 ? ((annualReturn / annualInvestment) * 100) : 0;
                  const paybackMonths = monthlyNetBenefit > 0 ? (annualInvestment / monthlyNetBenefit) : 0;

                  return (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <p className="font-medium text-slate-800 mb-3">Monthly Breakdown</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">FICA Savings</span>
                              <span className="font-medium text-emerald-700">${monthlyFicaSavings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">- Admin Fee</span>
                              <span className="font-medium text-slate-700">-${monthlyFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="h-px bg-slate-200 my-1" />
                            <div className="flex items-center justify-between">
                              <span className="text-slate-700">Gross Benefit</span>
                              <span className="font-semibold text-slate-900">${monthlyGrossBenefit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">- Contrib</span>
                              <span className="font-medium text-slate-700">-${monthlyContrib.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="h-px bg-slate-200 my-1" />
                            <div className="flex items-center justify-between rounded bg-emerald-100 px-2 py-1">
                              <span className="font-semibold text-emerald-800">Your Net</span>
                              <span className="font-bold text-emerald-800">${monthlyNetBenefit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <p className="font-medium text-slate-800 mb-3">Annual Breakdown</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">FICA Savings</span>
                              <span className="font-medium text-emerald-700">${(monthlyFicaSavings * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">- Admin Fee</span>
                              <span className="font-medium text-slate-700">-${(monthlyFee * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="h-px bg-slate-200 my-1" />
                            <div className="flex items-center justify-between">
                              <span className="text-slate-700">Gross Benefit</span>
                              <span className="font-semibold text-slate-900">${(monthlyGrossBenefit * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">- Contrib</span>
                              <span className="font-medium text-slate-700">-${(monthlyContrib * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            <div className="h-px bg-slate-200 my-1" />
                            <div className="flex items-center justify-between rounded bg-emerald-100 px-2 py-1">
                              <span className="font-semibold text-emerald-800">Your Net</span>
                              <span className="font-bold text-emerald-800">${(monthlyNetBenefit * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead className="font-semibold">Employee</TableHead>
                              <TableHead className="text-right font-semibold">FICA Savings</TableHead>
                              <TableHead className="text-right font-semibold">Fee</TableHead>
                              <TableHead className="text-right font-semibold">Contrib</TableHead>
                              <TableHead className="text-right font-semibold">Your Net (Monthly)</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {censusRows.map((row, idx) => (
                              <TableRow key={row.id || `${row.full_name || 'employee'}-${idx}`}>
                                <TableCell className="font-medium">{row.full_name || 'Unknown'}</TableCell>
                                <TableCell className="text-right text-emerald-700">
                                  ${Number(row.fica_tax_savings || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell className="text-right text-slate-700">
                                  -${Number(row.fee || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell className="text-right text-slate-700">
                                  -${Number(row.contribution || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell className="text-right font-semibold text-purple-700">
                                  ${Number(row.employer_net || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                              <TableCell>TOTAL</TableCell>
                              <TableCell className="text-right text-emerald-800">
                                ${monthlyFicaSavings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell className="text-right text-slate-800">
                                -${monthlyFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell className="text-right text-slate-800">
                                -${monthlyContrib.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell className="text-right text-purple-800">
                                ${monthlyNetBenefit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <p className="font-medium text-slate-800 mb-3">ROI Calculation</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-600">Annual Investment (admin fees)</span>
                            <span className="font-medium text-slate-900">
                              ${annualInvestment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-600">Annual Return (net savings)</span>
                            <span className="font-medium text-emerald-700">
                              ${annualReturn.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                          <div className="h-px bg-slate-200 my-1" />
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-slate-700">ROI</span>
                            <span className="font-bold text-emerald-800">

                              {annualInvestment > 0 && monthlyNetBenefit > 0
                                ? `${paybackMonths.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} months`
                                : 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="comparison" className="mt-6 space-y-6">
          {selectedProposal && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-500" />
                  With vs Without Section 125
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Optional comparison view based on current proposal census calculations.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {(() => {
                  const monthlyEmployeeTaxSavings = censusRows.reduce((sum, row) => sum + toSafeNumber(row.total_tax_savings), 0);
                  const monthlyEmployerNet = censusRows.reduce((sum, row) => sum + toSafeNumber(row.employer_net), 0);
                  const monthlyFicaSavings = censusRows.reduce((sum, row) => sum + toSafeNumber(row.fica_tax_savings), 0);

                  const withSection125 = {
                    employeeTaxSavingsMonthly: monthlyEmployeeTaxSavings,
                    employeeTaxSavingsAnnual: monthlyEmployeeTaxSavings * 12,
                    employerNetMonthly: monthlyEmployerNet,
                    employerNetAnnual: monthlyEmployerNet * 12,
                    employerFicaMonthly: monthlyFicaSavings,
                    employerFicaAnnual: monthlyFicaSavings * 12
                  };

                  const withoutSection125 = {
                    employeeTaxSavingsMonthly: 0,
                    employeeTaxSavingsAnnual: 0,
                    employerNetMonthly: 0,
                    employerNetAnnual: 0,
                    employerFicaMonthly: 0,
                    employerFicaAnnual: 0
                  };

                  const delta = {
                    employeeTaxSavingsMonthly: withSection125.employeeTaxSavingsMonthly - withoutSection125.employeeTaxSavingsMonthly,
                    employeeTaxSavingsAnnual: withSection125.employeeTaxSavingsAnnual - withoutSection125.employeeTaxSavingsAnnual,
                    employerNetMonthly: withSection125.employerNetMonthly - withoutSection125.employerNetMonthly,
                    employerNetAnnual: withSection125.employerNetAnnual - withoutSection125.employerNetAnnual,
                    employerFicaMonthly: withSection125.employerFicaMonthly - withoutSection125.employerFicaMonthly,
                    employerFicaAnnual: withSection125.employerFicaAnnual - withoutSection125.employerFicaAnnual
                  };

                  return (
                    <div className="space-y-4">
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                        <p className="font-medium">Assumptions used</p>
                        <ul className="list-disc ml-5 mt-1 space-y-1">
                          <li>"Without Section 125" baseline is modeled as zero tax-savings and zero Section 125-related employer impact.</li>
                          <li>"With Section 125" values are sourced from computed census row fields in this proposal.</li>
                          <li>Deltas represent incremental impact attributable to Section 125.</li>
                        </ul>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <p className="font-semibold text-slate-800 mb-3">Without Section 125</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between"><span>Employee Tax Savings / mo</span><span className="font-medium">{formatCurrency(withoutSection125.employeeTaxSavingsMonthly)}</span></div>
                            <div className="flex justify-between"><span>Employee Tax Savings / yr</span><span className="font-medium">{formatCurrency(withoutSection125.employeeTaxSavingsAnnual)}</span></div>
                            <div className="flex justify-between"><span>Employer Net / mo</span><span className="font-medium">{formatCurrency(withoutSection125.employerNetMonthly)}</span></div>
                            <div className="flex justify-between"><span>Employer Net / yr</span><span className="font-medium">{formatCurrency(withoutSection125.employerNetAnnual)}</span></div>
                            <div className="flex justify-between"><span>Employer FICA Savings / mo</span><span className="font-medium">{formatCurrency(withoutSection125.employerFicaMonthly)}</span></div>
                            <div className="flex justify-between"><span>Employer FICA Savings / yr</span><span className="font-medium">{formatCurrency(withoutSection125.employerFicaAnnual)}</span></div>
                          </div>
                        </div>

                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                          <p className="font-semibold text-emerald-800 mb-3">With Section 125</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between"><span>Employee Tax Savings / mo</span><span className="font-medium">{formatCurrency(withSection125.employeeTaxSavingsMonthly)}</span></div>
                            <div className="flex justify-between"><span>Employee Tax Savings / yr</span><span className="font-medium">{formatCurrency(withSection125.employeeTaxSavingsAnnual)}</span></div>
                            <div className="flex justify-between"><span>Employer Net / mo</span><span className="font-medium">{formatCurrency(withSection125.employerNetMonthly)}</span></div>
                            <div className="flex justify-between"><span>Employer Net / yr</span><span className="font-medium">{formatCurrency(withSection125.employerNetAnnual)}</span></div>
                            <div className="flex justify-between"><span>Employer FICA Savings / mo</span><span className="font-medium">{formatCurrency(withSection125.employerFicaMonthly)}</span></div>
                            <div className="flex justify-between"><span>Employer FICA Savings / yr</span><span className="font-medium">{formatCurrency(withSection125.employerFicaAnnual)}</span></div>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                        <p className="font-semibold text-blue-800 mb-3">Incremental Difference (With - Without)</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div className="flex justify-between rounded bg-white p-2 border"><span>Employee Tax Savings / mo</span><span className="font-semibold text-emerald-700">{formatCurrency(delta.employeeTaxSavingsMonthly)}</span></div>
                          <div className="flex justify-between rounded bg-white p-2 border"><span>Employee Tax Savings / yr</span><span className="font-semibold text-emerald-700">{formatCurrency(delta.employeeTaxSavingsAnnual)}</span></div>
                          <div className="flex justify-between rounded bg-white p-2 border"><span>Employer Net / mo</span><span className="font-semibold text-purple-700">{formatCurrency(delta.employerNetMonthly)}</span></div>
                          <div className="flex justify-between rounded bg-white p-2 border"><span>Employer Net / yr</span><span className="font-semibold text-purple-700">{formatCurrency(delta.employerNetAnnual)}</span></div>
                          <div className="flex justify-between rounded bg-white p-2 border"><span>Employer FICA Savings / mo</span><span className="font-semibold text-blue-700">{formatCurrency(delta.employerFicaMonthly)}</span></div>
                          <div className="flex justify-between rounded bg-white p-2 border"><span>Employer FICA Savings / yr</span><span className="font-semibold text-blue-700">{formatCurrency(delta.employerFicaAnnual)}</span></div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="employees" className="mt-6 space-y-6">
          {selectedProposal && (
            <>
              {/* Bell Landscaping 4-card summary */}
              <ProposalReportSummary
                summary={selectedProposal?.proposal_summary}
                notEligibleCount={censusRows.filter(r => !Number(r.flex_allocation)).length}
              />

              <Card>
                <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-emerald-500" />
                      Proposal Report
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      {selectedProposal.company_name} • {censusRows.length} employee{censusRows.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 self-start sm:self-auto">
                    <Button variant="outline" size="sm" onClick={() => {
                      if (censusRows.length === 0) {
                        toast.warning('No census data to export');
                        return;
                      }
                      // Columns whose value is empty/zero across every row are dropped
                      // from the export entirely, rather than shipping a column of blanks.
                      const csvColumns = [
                        { label: 'Employee', get: (r) => r.full_name || '' },
                        { label: 'Email', get: (r) => r.email || '' },
                        { label: 'Annual Salary', get: (r) => r.annual_salary || '' },
                        { label: 'NW4', get: (r) => r.nw4 || 'N' },
                        { label: 'F', get: (r) => {
                          const fs = String(r.filing_status || '').toLowerCase();
                          return r.filing_status_code || (fs.startsWith('m') ? 'M' : fs.startsWith('h') ? 'H' : 'S');
                        } },
                        { label: '#', get: (r) => r.w4_allowances ?? '' },
                        { label: '$', get: (r) => r.w4_step3_total || '' },
                        { label: 'State', get: (r) => r.state || '' },
                        { label: 'Section 125 / mo', get: (r) => r.flex_allocation || 0 },
                        { label: 'Your Net / mo', get: (r) => r.net_flex || 0 },
                        { label: 'Your Annual Benefit', get: (r) => Number(r.net_flex || 0) * 12 },
                        { label: 'Fee / mo', get: (r) => r.fee || 0 },
                        { label: 'Contrib / mo', get: (r) => r.contribution || 0 },
                        { label: 'Employer Net / mo', get: (r) => r.employer_net || 0 },
                        { label: 'Annualized', get: (r) => r.annualized || Number(r.employer_net || 0) * 12 },
                      ];
                      const visibleCsvColumns = csvColumns.filter((col) => censusRows.some((row) => {
                        const v = col.get(row);
                        return v !== '' && v !== null && v !== undefined && Number(v) !== 0;
                      }));
                      const csvHeaders = visibleCsvColumns.map((col) => col.label);
                      const csvRows = censusRows.map((row) =>
                        visibleCsvColumns.map((col) => `"${col.get(row) ?? ''}"`).join(',')
                      );
                      const csvContent = [csvHeaders.join(','), ...csvRows].join('\n');
                      const blob = new Blob([csvContent], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `proposal_report_${selectedProposal.reference_id || 'export'}.csv`;
                      link.click();
                      URL.revokeObjectURL(url);
                      toast.success('Report exported');
                    }}>
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleEmployeeListPdfDownload} disabled={employeeListPdfLoading}>
                      {employeeListPdfLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Download PDF
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {censusRows.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No census data loaded for this proposal.</p>
                      <p className="text-sm mt-1">Upload census data in the Proposal Wizard to see employee details.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Search */}
                      <div className="flex items-center gap-3">
                        <Input
                          placeholder="Search employees by name, email, or state..."
                          value={employeeSearch}
                          onChange={(e) => setEmployeeSearch(e.target.value)}
                          className="w-full sm:max-w-md"
                        />
                        {employeeSearch && (
                          <Button variant="ghost" size="sm" onClick={() => setEmployeeSearch('')}>
                            Clear
                          </Button>
                        )}
                      </div>

                      {/* Bell Landscaping Report Table */}
                      <TooltipProvider delayDuration={300}>
                      <div
                        ref={tableScrollRef}
                        className="overflow-x-auto rounded-lg border"
                        onMouseMove={handleTableMouseMove}
                        onMouseLeave={stopTableScroll}
                      >
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              {[
                                { key: 'full_name',          label: 'Employee',           title: 'Employee',                         align: 'left'   },
                                { key: 'annual_salary',      label: 'Annual Salary',       title: 'Annual Salary',                    align: 'right'  },
                                { key: 'nw4',                label: 'NW4',                 title: 'New W4 Form (Post-2020 = Y)',      align: 'center' },
                                { key: 'filing_status_code', label: 'F',                   title: 'Filing Status (S / M / H)',        align: 'center' },
                                { key: 'w4_allowances',      label: '#',                   title: 'W4 Allowances / Dependents',       align: 'center' },
                                { key: 'w4_step3_total',     label: '$',                   title: 'New W4 Step 3 Credits ($)',        align: 'right'  },
                                { key: 'state',              label: 'State',               title: 'State',                            align: 'center' },
                                { key: 'flex_allocation',    label: 'Section 125 / mo',    title: 'Section 125 Monthly Premium',      align: 'right'  },
                                { key: 'net_flex',           label: 'Your Net / mo',       title: 'Your Net Monthly Benefit',         align: 'right'  },
                                { key: 'net_flex_annual',    label: 'Your Annual Benefit', title: 'Your Annual Benefit',              align: 'right'  },
                                { key: 'fee',                label: 'Fee / mo',            title: 'Employer Fee per Employee (Monthly)', align: 'right' },
                                { key: 'contribution',       label: 'Contrib / mo',        title: 'Employer Contribution / Top-Up (Monthly)', align: 'right' },
                                { key: 'employer_net',       label: 'Employer Net / mo',   title: 'Employer Net Monthly',             align: 'right'  },
                                { key: 'annualized',         label: 'Annualized',          title: 'Employer Net Annualized (12 × monthly)', align: 'right' },
                              ].map((col) => (
                                <TableHead
                                  key={col.key}
                                  title={col.title}
                                  className={`font-semibold cursor-pointer select-none hover:bg-slate-100 transition-colors whitespace-nowrap py-2 px-3 text-sm ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''}`}
                                  onClick={() => setEmployeeSort(prev => ({ key: col.key, dir: prev.key === col.key && prev.dir === 'asc' ? 'desc' : 'asc' }))}
                                >
                                  {col.label}
                                  {employeeSort.key === col.key && (
                                    <span className="text-emerald-600 ml-0.5">{employeeSort.dir === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </TableHead>
                              ))}
                              <TableHead className="text-center font-semibold text-sm py-2 px-2">Details</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(() => {
                              return sortedEmployeeRows.map((row, idx) => {
                                const nw4 = row.nw4 || 'N';
                                const fs = String(row.filing_status || '').toLowerCase();
                                const fCode = row.filing_status_code || (fs.startsWith('m') ? 'M' : fs.startsWith('h') ? 'H' : 'S');
                                const fTitle = fs.startsWith('m') ? 'Married' : fs.startsWith('h') ? 'Head of Household' : 'Single';
                                const td = 'py-2 px-3 text-sm';
                                return (
                                  <TableRow
                                    key={row.id || idx}
                                    className="cursor-pointer hover:bg-emerald-50 transition-colors"
                                    onClick={() => { setSelectedEmployee(row); setEmployeeModalOpen(true); }}
                                  >
                                    {/* Employee */}
                                    <TableCell className={td}>
                                      <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm flex-shrink-0">
                                          {(row.full_name || '?').charAt(0).toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                          <Tip label={row.full_name || 'Unknown'} side="top">
                                            <p className="font-medium text-slate-800 truncate max-w-[160px] cursor-default">{row.full_name || 'Unknown'}</p>
                                          </Tip>
                                          <Tip label={<span className="flex items-center gap-1.5"><Mail className="w-3 h-3" />{row.email || '—'}</span>} side="bottom">
                                            <p className="text-slate-400 truncate max-w-[160px] cursor-default">{row.email || '—'}</p>
                                          </Tip>
                                        </div>
                                      </div>
                                    </TableCell>
                                    {/* Annual Salary */}
                                    <TableCell className={`${td} text-right`}>
                                      {row.annual_salary ? `$${Number(row.annual_salary).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '-'}
                                    </TableCell>
                                    {/* NW4 */}
                                    <TableCell className={`${td} text-center`}>
                                      <Tip label={nw4 === 'Y' ? 'New W4 Form (Post-2020)' : 'Old W4 Form (Pre-2020)'}>
                                        <span className={`font-semibold cursor-default ${nw4 === 'Y' ? 'text-blue-600' : 'text-slate-500'}`}>{nw4}</span>
                                      </Tip>
                                    </TableCell>
                                    {/* F - Filing code */}
                                    <TableCell className={`${td} text-center`}>
                                      <Tip label={fTitle}>
                                        <span className="cursor-default">{fCode}</span>
                                      </Tip>
                                    </TableCell>
                                    {/* # - Allowances */}
                                    <TableCell className={`${td} text-center`}>
                                      <Tip label={`${row.w4_allowances ?? 0} W4 Allowances`}>
                                        <span className="cursor-default">{row.w4_allowances ?? '-'}</span>
                                      </Tip>
                                    </TableCell>
                                    {/* $ - New W4 Step 3 credits */}
                                    <TableCell className={`${td} text-right text-slate-500`}>
                                      <Tip label="New W4 Step 3 Credits (dependent deductions)">
                                        <span className="cursor-default">{Number(row.w4_step3_total) > 0 ? formatCurrency(row.w4_step3_total) : '-'}</span>
                                      </Tip>
                                    </TableCell>
                                    {/* State */}
                                    <TableCell className={`${td} text-center uppercase text-slate-500`}>{row.state || '-'}</TableCell>
                                    {/* Section 125 / mo */}
                                    <TableCell className={`${td} text-right font-medium text-blue-600`}>
                                      <Tip label="Optimal Section 125 Monthly Premium">
                                        <span className="cursor-default">{Number(row.flex_allocation) > 0 ? formatCurrency(row.flex_allocation) : '-'}</span>
                                      </Tip>
                                    </TableCell>
                                    {/* Your Net / mo */}
                                    <TableCell className={`${td} text-right font-medium text-emerald-600`}>
                                      <Tip label="Your net monthly benefit after unreimbursed portion">
                                        {/* Gated on eligibility (flex_allocation), not net_flex's own sign —
                                            net_flex can legitimately be zero or negative (unreimbursed portion
                                            exceeding tax savings) and that real number must still show, not '-'. */}
                                        <span className="cursor-default">{Number(row.flex_allocation) > 0 ? formatCurrency(row.net_flex) : '-'}</span>
                                      </Tip>
                                    </TableCell>
                                    {/* Your Annual Benefit */}
                                    <TableCell className={`${td} text-right`}>
                                      <Tip label="Your total annual benefit (12 × monthly)">
                                        <span className="cursor-default">{Number(row.flex_allocation) > 0 ? formatCurrency(Number(row.net_flex) * 12) : '-'}</span>
                                      </Tip>
                                    </TableCell>
                                    {/* Fee / mo */}
                                    <TableCell className={`${td} text-right text-slate-600`}>
                                      <Tip label="Employer fee per employee (monthly)">
                                        <span className="cursor-default">{Number(row.fee) > 0 ? formatCurrency(row.fee) : '-'}</span>
                                      </Tip>
                                    </TableCell>
                                    {/* Contribution / mo */}
                                    <TableCell className={`${td} text-right text-slate-600`}>
                                      <Tip label="Employer contribution / top-up (monthly)">
                                        <span className="cursor-default">{Number(row.contribution) > 0 ? formatCurrency(row.contribution) : '-'}</span>
                                      </Tip>
                                    </TableCell>
                                    {/* Employer Net / mo */}
                                    <TableCell className={`${td} text-right font-semibold text-purple-700`}>
                                      <Tip label="Employer net monthly savings from Section 125 plan">
                                        {/* Gated on eligibility, not employer_net's own sign — a negative
                                            employer_net (admin fee exceeding FICA savings) is a real, important
                                            outcome and must display, not be hidden as '-'. */}
                                        <span className="cursor-default">{Number(row.flex_allocation) > 0 ? formatCurrency(row.employer_net) : '-'}</span>
                                      </Tip>
                                    </TableCell>
                                    {/* Annualized */}
                                    <TableCell className={`${td} text-right font-semibold text-purple-700`}>
                                      <Tip label="Employer net annualized (12 × monthly)">
                                        <span className="cursor-default">{Number(row.flex_allocation) > 0 ? formatCurrency(row.annualized || Number(row.employer_net) * 12) : '-'}</span>
                                      </Tip>
                                    </TableCell>
                                    {/* Details */}
                                    <TableCell className={`${td} text-center`}>
                                      <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={(e) => { e.stopPropagation(); setSelectedEmployee(row); setEmployeeModalOpen(true); }}>
                                        View
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              });
                            })()}
                            {/* Totals Row */}
                            <TableRow className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                              <TableCell className="py-2 px-3 font-bold">{censusRows.length} Employees</TableCell>
                              <TableCell className="py-2 px-3 text-right">
                                ${censusRows.reduce((s, r) => s + Number(r.annual_salary || 0), 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                              </TableCell>
                              <TableCell className="py-2 px-3 text-center">—</TableCell>
                              <TableCell className="py-2 px-3 text-center">—</TableCell>
                              <TableCell className="py-2 px-3 text-center">—</TableCell>
                              <TableCell className="py-2 px-3 text-center">—</TableCell>
                              <TableCell className="py-2 px-3 text-center">—</TableCell>
                              <TableCell className="py-2 px-3 text-right text-blue-700">
                                {formatCurrency(censusRows.reduce((s, r) => s + Number(r.flex_allocation || 0), 0))}
                              </TableCell>
                              <TableCell className="py-2 px-3 text-right text-emerald-700">
                                Avg {formatCurrency(censusRows.length ? censusRows.reduce((s, r) => s + Number(r.net_flex || 0), 0) / censusRows.length : 0)}
                              </TableCell>
                              <TableCell className="py-2 px-3 text-right">
                                Avg {formatCurrency(censusRows.length ? censusRows.reduce((s, r) => s + Number(r.net_flex || 0), 0) * 12 / censusRows.length : 0)}
                              </TableCell>
                              <TableCell className="py-2 px-3 text-right text-slate-700">
                                Total {formatCurrency(censusRows.reduce((s, r) => s + Number(r.fee || 0), 0))}
                              </TableCell>
                              <TableCell className="py-2 px-3 text-right text-slate-700">
                                Total {formatCurrency(censusRows.reduce((s, r) => s + Number(r.contribution || 0), 0))}
                              </TableCell>
                              <TableCell className="py-2 px-3 text-right text-purple-700">
                                Total {formatCurrency(censusRows.reduce((s, r) => s + Number(r.employer_net || 0), 0))}
                              </TableCell>
                              <TableCell className="py-2 px-3 text-right text-purple-700">
                                Total {formatCurrency(censusRows.reduce((s, r) => s + Number(r.annualized || 0), 0))}
                              </TableCell>
                              <TableCell className="py-2 px-3">—</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                      </TooltipProvider>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Employee Detail Modal */}
      <Dialog open={employeeModalOpen} onOpenChange={setEmployeeModalOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-lg">
                {selectedEmployee?.full_name?.charAt(0).toUpperCase() || '?'}
              </div>
              <div>
                <div className="text-lg font-semibold">{selectedEmployee?.full_name || 'Employee'}</div>
                <div className="text-sm font-normal text-slate-500">
                  {selectedEmployee?.annual_salary ? `${Number(selectedEmployee.annual_salary).toLocaleString()} salary` : ''}
                  {selectedEmployee?.filing_status ? ` • ${selectedEmployee.filing_status}` : ''}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          {selectedEmployee && (
            <div className="space-y-6">
              {/* Employee Profile */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-800 mb-3">Employee Profile</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Annual Salary</p>
                    <p className="font-medium">
                      ${selectedEmployee.annual_salary 
                        ? Number(selectedEmployee.annual_salary).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Monthly Salary</p>
                    <p className="font-medium">
                      ${selectedEmployee.annual_salary 
                        ? (Number(selectedEmployee.annual_salary) / 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Filing Status</p>
                    <p className="font-medium capitalize">{selectedEmployee.filing_status || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">State</p>
                    <p className="font-medium uppercase">{selectedEmployee.state || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">W4 Type (NW4)</p>
                    <p className="font-medium">{selectedEmployee.nw4 === 'Y' ? 'New W4 (Post-2020)' : 'Old W4 (Pre-2020)'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">W4 Allowances (#)</p>
                    <p className="font-medium">{selectedEmployee.w4_allowances ?? '0'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Step 3 Credits ($)</p>
                    <p className="font-medium">{Number(selectedEmployee.w4_step3_total) > 0 ? formatCurrency(selectedEmployee.w4_step3_total) : '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Pre-Tax Monthly (Box)</p>
                    <p className="font-medium">{Number(selectedEmployee.pre_tax_monthly) > 0 ? formatCurrency(selectedEmployee.pre_tax_monthly) : '—'}</p>
                  </div>
                </div>
              </div>

              {/* Section 125 Contribution */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-3">Section 125 Contribution</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-blue-600">Monthly Amount</p>
                    <p className="text-xl font-bold text-blue-700">
                      ${selectedEmployee.flex_allocation 
                        ? Number(selectedEmployee.flex_allocation).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '0.00'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-600">Annual Amount</p>
                    <p className="text-xl font-bold text-blue-700">
                      ${selectedEmployee.flex_allocation 
                        ? (Number(selectedEmployee.flex_allocation) * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '0.00'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tax Impact */}
              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-semibold text-emerald-800 mb-3">Tax Impact</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-emerald-100">
                    <span className="text-slate-600">Federal Tax Savings</span>
                    <span className="font-medium text-emerald-700">
                      ${selectedEmployee.federal_tax_savings 
                        ? Number(selectedEmployee.federal_tax_savings).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '0.00'}/mo
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-emerald-100">
                    <span className="text-slate-600">FICA Tax Savings</span>
                    <span className="font-medium text-emerald-700">
                      ${selectedEmployee.fica_tax_savings 
                        ? Number(selectedEmployee.fica_tax_savings).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '0.00'}/mo
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-emerald-100">
                    <span className="text-slate-600">State Tax Savings</span>
                    <span className="font-medium text-emerald-700">
                      ${selectedEmployee.state_tax_savings 
                        ? Number(selectedEmployee.state_tax_savings).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '0.00'}/mo
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 font-semibold">
                    <span className="text-slate-800">Total Tax Savings</span>
                    <span className="text-lg text-emerald-700">
                      ${selectedEmployee.total_tax_savings 
                        ? Number(selectedEmployee.total_tax_savings).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : (selectedEmployee.federal_tax_savings && selectedEmployee.fica_tax_savings 
                          ? (Number(selectedEmployee.federal_tax_savings) + Number(selectedEmployee.fica_tax_savings)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : '0.00')}/mo
                    </span>
                  </div>
                </div>
              </div>

              {/* Your Benefit as Employee */}
              <div className="bg-amber-50 rounded-lg p-4">
                <h4 className="font-semibold text-amber-800 mb-3">Your Net Benefit (Employee)</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-amber-100">
                    <span className="text-slate-600">Tax Savings (Fed + FICA + State)</span>
                    <span className="font-medium text-amber-700">
                      ${selectedEmployee.total_tax_savings 
                        ? Number(selectedEmployee.total_tax_savings).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '0.00'}/mo
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-amber-100">
                    <span className="text-slate-600">Premium Credit Applied</span>
                    <span className="font-medium text-blue-700">
                      ${selectedEmployee.flex_allocation 
                        ? Number(selectedEmployee.flex_allocation).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '0.00'}/mo
                    </span>
                  </div>
                  {selectedEmployee.unreimbursed && Number(selectedEmployee.unreimbursed) > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-amber-100">
                      <span className="text-slate-600">Unreimbursed Portion</span>
                      <span className="font-medium text-slate-500">
                        -${Number(selectedEmployee.unreimbursed).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-2 font-semibold bg-amber-100 rounded px-3 -mx-3">
                    <span className="text-amber-800">Employee Net Benefit</span>
                    <span className="text-xl text-amber-700">
                      ${selectedEmployee.net_benefit 
                        ? Number(selectedEmployee.net_benefit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '0.00'}/mo
                    </span>
                  </div>
                </div>
              </div>

              {/* Your Benefit as Employer */}
              <div className="bg-purple-50 rounded-lg p-4">
                <h4 className="font-semibold text-purple-800 mb-3">Your Benefit as Employer</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-purple-100">
                    <span className="text-slate-600">FICA Savings on Section 125</span>
                    <span className="font-medium text-purple-700">
                      ${selectedEmployee.employer_fica_savings 
                        ? Number(selectedEmployee.employer_fica_savings).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : (selectedEmployee.fica_tax_savings 
                          ? Number(selectedEmployee.fica_tax_savings).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : '0.00')}/mo
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-purple-100">
                    <span className="text-slate-600">Administrative Fee</span>
                    <span className="font-medium text-slate-500">
                      -${Number(selectedEmployee.fee ?? selectedEmployee.admin_fee ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-purple-100">
                    <span className="text-slate-600">Contribution</span>
                    <span className="font-medium text-slate-500">
                      -${Number(selectedEmployee.contribution ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 font-semibold bg-purple-100 rounded px-3 -mx-3">
                    <span className="text-purple-800">Your Net Monthly Benefit</span>
                    <span className="text-xl text-purple-700">
                      ${selectedEmployee.employer_net 
                        ? Number(selectedEmployee.employer_net).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '0.00'}/mo
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3 justify-end pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={handleEmployeePdfDownload}
                  disabled={employeePdfLoading || employeeEmailLoading}
                >
                  {employeePdfLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Download PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={handleEmployeeEmailSend}
                  disabled={employeeEmailLoading || employeePdfLoading}
                >
                  {employeeEmailLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Email to Employee
                </Button>
                <Button variant="outline" onClick={() => setEmployeeModalOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
